import { useState, useEffect, useCallback, Fragment, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ScanSearch,
  Volume2,
  Mic,
  BarChart3,
  Phone,
  RefreshCw,
  Loader2,
  Shield,
  Globe,
  Brain,
  Zap,
  Sparkles,
  Lock,
  Layers,
  Database,
  AlertTriangle,
  AlertCircle,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { ServiceProviderSelect, type AvailableProviderEntry, type ProviderAvailability } from '@easyflow/ui-ai-routing';
import {
  SERVICE_CAPABILITY_MAP,
  isServicePlatformScoped as isServicePlatformScopedShared,
  toErr,
  AiProviderStatusResponseSchema,
  CUSTOM_ENDPOINT_PRESETS,
  RerankModeStatusSchema,
} from '@easyflow/shared';
import { useToast } from '@/hooks/use-toast';
import { useEmbedderGate } from '@/hooks/useEmbedderGate';
import { useChatModelOptions } from '@/hooks/useChatModelOptions';
import { apiClient, formatApiErrorMessage } from '@/lib/api';
import RAGPlusSection from './RAGPlusSection';
import AiModelsCatalogSection from './AiModelsCatalogSection';
import AiGovernanceHistory from './AiGovernanceHistory';
import { useAuth } from '@/contexts/AuthContext';
import ServiceModelPicker, { clearSharedPickerReads } from './TenantServiceModelPicker';
import OtherPlatformServicesSection from './OtherPlatformServicesSection';
import EmbedderGateOverlay from './EmbedderGateOverlay';
import RoutingAccordion, { type RoutingAccent } from './RoutingAccordion';
import RoutingGuideDialog from './RoutingGuideDialog';
import FunctionHelp, { type FunctionHelpContent } from './FunctionHelp';
import ProviderOverview, {
  type ProviderOverviewEntry,
  type ProviderOverviewStatus,
} from './ProviderOverview';
import type { AiProviderStatusResponse, RerankModeStatus } from '@easyflow/shared';

// Predicato platform-scoped centralizzato in `@easyflow/shared`
// (`isServicePlatformScoped`). Wrapper locale conservato per minimizzare
// il diff sui call site; default per service ignoti = platform-scoped
// (più restrittivo del default shared `false`, coerente con la semantica
// storica del componente).
function isServicePlatformScoped(serviceKey: string): boolean {
  const d = SERVICE_CAPABILITY_MAP.find((s) => s.service_key === serviceKey);
  if (d) return isServicePlatformScopedShared(serviceKey);
  return true;
}
const PLATFORM_SCOPED_READONLY_REASON =
  'Modello gestito a livello piattaforma. Modificabile solo da super admin in Master.';

// Mapping ServiceItem.key (UI) → platform_model_config.service_key.
// Solo i servizi che hanno una riga in platform_model_config: per gli altri
// (voice_agents holder, stt/tts toggle, …) il picker non viene renderizzato.
const PLATFORM_SERVICE_KEY_MAP: Record<string, string> = {
  embeddings:               'embedding',
  document_parsing:         'document_parsing',
  ocr:                      'ocr',
  rag_decomposer:           'rag_decomposition',
  rag_reranker:             'rag_rerank',
  // Secondario cross-check rerank (tenant-scoped).
  rag_rerank_secondary:     'rag_rerank_secondary',
  rag_synthesis:            'rag_synthesis',
  rag_synthesis_multimodal: 'rag_synthesis_multimodal',
  rag_synthesis_multimodal_fallback: 'rag_synthesis_multimodal_fallback',
  tts:                      'tts_ivr',
  conversation_analysis:    'conversation_analysis',
};

// Sotto-righe della card consolidata "Speech-to-Text" nel gruppo Voice.
// Ognuna è una riga indipendente provider+modello su platform_model_config.
interface STTSubRow {
  key: string;            // platform_model_config.service_key
  label: string;
  desc: string;
  longDescription: ServiceItem['longDescription'];
}
const STT_SUBROWS: ReadonlyArray<STTSubRow> = [
  {
    key: 'stt_realtime',
    label: 'STT Realtime (transcript live agenti)',
    desc: 'Transcript testuale generato in streaming dagli agenti OpenAI Realtime per UI/log/RAG. Non è l\'ASR live.',
    longDescription: {
      whatItDoes: 'Genera il transcript testuale "secondo canale" durante una sessione voice agent (OpenAI Realtime), così che la UI mostri la conversazione in chiaro e il backend la indicizzi nel RAG/log.',
      whenItRuns: 'In streaming durante la chiamata Realtime. Non blocca l\'audio: gira in parallelo all\'ASR nativo del provider.',
      impact: 'Permette ricerca testuale, summary automatico, alimentazione RAG con le conversazioni passate. Nessun impatto sulla latenza voce-a-voce.',
      notes: '🚫 NON è l\'ASR vero del voice agent (gira nativamente dentro OpenAI Realtime, Gemini Live, ElevenLabs). Quello non passa da qui.',
    },
  },
  {
    key: 'stt_voicemail',
    label: 'STT Voicemail',
    desc: 'Trascrizione asincrona dei messaggi vocali lasciati nelle voicemail.',
    longDescription: {
      whatItDoes: 'Riceve l\'audio voicemail salvato e ne produce il testo, salvato accanto al record voicemail per lettura UI e indicizzazione RAG.',
      whenItRuns: 'Asincrono dopo il salvataggio della voicemail. Non blocca il flusso chiamata.',
      impact: 'Permette di leggere le voicemail invece di ascoltarle, ricercare il contenuto nei log, alimentare il RAG.',
      notes: 'Whisper (OpenAI). Nessun impatto su agenti voce live.',
    },
  },
  {
    key: 'stt_video',
    label: 'STT Video / Meeting',
    desc: 'Trascrizione post-upload di file video e registrazioni meeting con speaker diarization e timestamp.',
    longDescription: {
      whatItDoes: 'Trascrive in testo i file video caricati e le registrazioni dei meeting/conferenze con speaker diarization e timestamp.',
      whenItRuns: 'Post-processing dopo l\'upload del video o la fine di un meeting registrato. Asincrono in coda di lavoro.',
      impact: 'Rende cercabile il contenuto dei video, alimenta il RAG con le riunioni passate, abilita riassunti automatici e ricerca puntuale.',
      notes: 'Whisper Large v3 (OpenAI). NON è VAD live.',
    },
  },
];

/** Funzioni voce il cui provider effettivo si legge da /ai-tools/service-models. */
const VOICE_ROUTED_KEYS: ReadonlySet<string> = new Set(['tts_ivr', ...STT_SUBROWS.map((s) => s.key)]);

/**
 * Funzioni RAG che a runtime usano il modello chat dell'embedder
 * (rag-chat-model.service: getRagChatModel vince sulla riga del servizio).
 */
const RAG_CHAT_FOLLOWER_KEYS: ReadonlySet<string> = new Set(['rag_synthesis', 'rag_decomposer']);

function embedderChatModelOf(embedder: 'openai' | 'gemini' | null, gemini: string, openai: string): string | null {
  const raw = embedder === 'gemini' ? gemini : embedder === 'openai' ? openai : '';
  return raw.trim() ? raw.trim() : null;
}

/** Provider effettivo del modello chat dell'embedder, se impostato. */
function embedderChatProvider(embedder: 'openai' | 'gemini' | null, model: string | null): 'openai' | 'google' | null {
  if (!embedder || !model) return null;
  return embedder === 'gemini' ? 'google' : 'openai';
}

const RERANK_MODE_HELP: FunctionHelpContent = {
  whatItDoes: 'Dopo la ricerca, riordina i passaggi recuperati in base alla pertinenza reale con la domanda. È il salto di qualità più grande della pipeline RAG.',
  points: [
    { label: 'Disattivato', text: 'nessun riordino: si usa l\'ordine della ricerca. Più veloce, meno accurato.' },
    { label: 'Modello singolo', text: 'un provider rilegge i passaggi con la domanda e li riordina. Provider e modello si scelgono nella riga "Reranker".' },
    { label: 'Cross-check', text: 'due provider diversi lavorano in parallelo e le due classifiche vengono fuse (Reciprocal Rank Fusion).' },
  ],
  whenItRuns: 'A ogni domanda, dopo la ricerca nella Knowledge Base e prima della risposta.',
  impact: 'Modello singolo: una chiamata in più per domanda. Cross-check: due chiamate e un po\' di latenza in più, in cambio della massima qualità.',
  advice: 'Accordo, latenze e costi reali del reranking sono nella scheda RAG Analytics.',
};

/** Provider ammessi dal motore di rerank (gli endpoint Custom AI non sono reranker). */
const RERANK_SELECTABLE: ReadonlySet<string> = new Set(['openai', 'claude', 'google', 'none']);

// `service_key` di platform_model_config governati altrove (card dedicate
// o card consolidata STT) e quindi esclusi dalla sezione "Altri servizi piattaforma".
const EXTRA_EXCLUDED_PLATFORM_KEYS: ReadonlyArray<string> = [
  'stt_realtime',
  'stt_voicemail',
  'stt_video',
  // workflow_builder: governato esclusivamente da WorkflowBuilderAIConfigCard
  // (provider+modello+temperature+max_tokens su platform_model_config.extra_config).
  'workflow_builder',
  // landing_image: generazione immagini Site Builder, governata dal picker
  // dedicato (TwoStepModelPicker capability="image" → catalog live filtrato per
  // required_features=image_generation) — non è un routing provider chat
  // generico, quindi fuori da "Altri servizi piattaforma".
  'landing_image',
  // Governate dalla sezione RAG Plus: interruttore, flag e
  // modello dell'espansione query si impostano lì, non due volte.
  'rag_quality_features',
  'rag_query_expansion',
  'rag_query_expansion_model',
  // Agente di commissioning: provider e modello nel suo modale di configurazione.
  'commissioning_builder',
  // Contesto dei frammenti e strategia Top-K: il runtime non legge provider e
  // modello di queste righe (usa la configurazione di piattaforma e il modello
  // chat dell'embedder), quindi una scelta qui non avrebbe effetto.
  'rag_chunk_context',
  'rag_topk_strategy',
];

interface OcrStatusState {
  provider: string;
  api_key_present: boolean;
  api_key_valid_format: boolean;
}

interface OcrCoherenceState {
  ocr_provider: string;
  chat_model: string | null;
  coherent: boolean;
  // Severità asimmetrica motivata: 'error' = bypass Gemini rotto
  // (hard), 'warn' = OpenAI senza coerenza spazio semantico (non bloccante).
  severity?: 'error' | 'warn';
  reason?: string;
}

interface OcrFailureEntry {
  id: number | string;
  severity: 'low' | 'medium' | 'high' | string;
  message: string;
  metric_value: number | null;
  created_at: string;
}

const PROVIDER_META: Record<string, { label: string; color: string; dotColor: string; icon: typeof Brain }> = {
  openai: { label: 'OpenAI', color: 'bg-sky-100 text-sky-700 border-sky-200', dotColor: 'bg-sky-500', icon: Zap },
  claude: { label: 'Claude', color: 'bg-purple-100 text-purple-700 border-purple-200', dotColor: 'bg-purple-500', icon: Brain },
  google: { label: 'Gemini', color: 'bg-blue-100 text-blue-700 border-blue-200', dotColor: 'bg-blue-500', icon: Globe },
  // Custom AI come provider first-class accanto ai 4 canonical.
  // Dot/colore ciano per distinguere a colpo d'occhio gli endpoint
  // tenant-scoped (`custom:<uuid>`); il label generico "Custom" viene
  // sovrascritto dal `label` reale ricevuto da `/available-providers`
  // ("<name> (custom)") quando il select renderizza la entry custom.
  custom: { label: 'Custom', color: 'bg-cyan-100 text-cyan-700 border-cyan-200', dotColor: 'bg-cyan-500', icon: Brain },
  none: { label: 'Disattivato', color: 'bg-gray-100 text-gray-500 border-gray-200', dotColor: 'bg-gray-400', icon: Shield },
};

/** Provider nativi nel pannello provider e relativa sotto-scheda di Provider AI. */
const CANONICAL_PROVIDER_CARDS: ReadonlyArray<{ key: 'openai' | 'claude' | 'google'; tone: ProviderOverviewEntry['tone'] }> = [
  { key: 'openai', tone: 'emerald' },
  { key: 'claude', tone: 'orange' },
  { key: 'google', tone: 'blue' },
];

/** Riga sotto "Custom AI": nome dell'endpoint, con il preset se diverso (es. "Router aziendale · OpenRouter"). */
function customEndpointDetail(name: string, kind: string): string {
  const preset = CUSTOM_ENDPOINT_PRESETS.find((p) => p.kind === kind)?.label;
  return preset && preset.toLowerCase() !== name.trim().toLowerCase() ? `${name} · ${preset}` : name;
}

// Suggerimenti dinamici pipeline RAG: chiave = servizio, valore = preferito +
// alternative + nota. Tipizzato per stato locale e come envelope di risposta.
type DynamicRecs = Record<string, {
  preferred: { provider: string; model: string; reason: string } | null;
  alternatives: Array<{ provider: string; model: string; reason: string }>;
  note: string | null;
}>;

type ServiceCategory = 'llm' | 'media';
type ServiceGroup = 'ingestion' | 'retrieval' | 'voice' | 'analytics';

interface ServiceItem {
  key: string;
  label: string;
  desc: string;
  icon: typeof Brain;
  category: ServiceCategory;
  group: ServiceGroup;
  /** Badge: "Core" se è essenziale per il funzionamento del gruppo */
  core?: boolean;
  /**
   * Provider unico non modificabile dalla riga (badge "fisso" al posto della select).
   * I provider selezionabili delle altre righe arrivano SOLO dal backend
   * (/ai-tools/available-providers): nessuna lista nel FE.
   */
  fixedProvider?: 'openai';
  /** La riga può essere spenta (provider 'none'). */
  canDisable?: boolean;
  recommendation: string;
  longDescription: {
    whatItDoes: string;
    whenItRuns: string;
    impact: string;
    notes?: string;
  };
}

/**
 * Riorganizzazione dash Routing AI per gruppi pipeline.
 * Aiuta l'admin a capire COSA succede in ogni fase del flusso end-to-end:
 *   1. Ingestion (caricamento KB)  → embeddings, document_parsing, ocr
 *   2. Retrieval & Reasoning (query) → decomposer, reranker, synthesis
 *   3. Voice & Conversation         → voice_agents, tts, stt, video_transcription
 *   4. Analytics & Post-Processing  → conversation_analysis
 */
const SERVICE_GROUPS: Record<ServiceGroup, { label: string; subtitle: string; icon: typeof Brain; accent: RoutingAccent }> = {
  ingestion: {
    label: 'Pipeline Ingestion (caricamento Knowledge Base)',
    subtitle: 'Cosa succede quando carichi un documento, immagine, audio o video nella KB. Modelli usati per estrarre testo, generare vettori e abilitare la ricerca semantica.',
    icon: ScanSearch,
    accent: 'sky',
  },
  retrieval: {
    label: 'Pipeline Retrieval & Reasoning (query utente)',
    subtitle: "Cosa succede quando l'utente fa una domanda all'agente RAG. Ogni servizio è un passo della pipeline: decomposizione → recupero → rerank → sintesi finale.",
    icon: Brain,
    accent: 'violet',
  },
  voice: {
    label: 'Voce & Conversazione',
    subtitle: 'Servizi audio: agenti voce live (SIP/WebRTC), TTS per annunci IVR, STT per voicemail, trascrizione video/meeting. Gli agenti voce live usano pipeline native, gli altri toggle qui valgono per voicemail/IVR/post-processing.',
    icon: Volume2,
    accent: 'emerald',
  },
  analytics: {
    label: 'Analytics & Post-Processing',
    subtitle: 'Modelli che girano DOPO la fine di una conversazione: sentiment, riassunti, classificazione automatica. Asincroni, non impattano la latenza live.',
    icon: BarChart3,
    accent: 'amber',
  },
};

// Embedder: passo 1 della pagina (card dedicata), descritto come le altre funzioni.
const EMBEDDINGS_ITEM: ServiceItem = {
  key: 'embeddings',
  label: 'Embedder (vettori KB)',
  desc: 'Modello che trasforma documenti e query in vettori per la ricerca semantica. È la base della pipeline RAG.',
  icon: ScanSearch,
  category: 'media',
  group: 'ingestion',
  core: true,
  fixedProvider: 'openai',
  recommendation: 'OpenAI text-embedding-3-large (1536 dim, text-only) o Gemini gemini-embedding-2 (3072 dim, multimodale nativo per image/audio/video). Solo OpenAI/Gemini supportati: Claude non offre embedding API.',
  longDescription: {
    whatItDoes: 'Trasforma testo (e con Gemini anche immagini/audio/video) in vettori numerici 1536 o 3072 dim che catturano il significato semantico. Permette la ricerca "per significato" anziché solo per parole chiave. È la fondazione di tutto il RAG: senza embedder coerente non c\'è retrieval.',
    whenItRuns: 'Due momenti: (1) Indicizzazione — quando carichi un documento nella KB viene chunkizzato (parent + child) e ogni chunk vettorializzato. (2) Query — ogni domanda utente viene vettorializzata per cercare i chunk più simili.',
    impact: 'Determina la qualità di tutto il retrieval. Con Gemini 3072 dim ottieni cross-modal nativo (una query testuale recupera un\'immagine pertinente nello stesso spazio semantico).',
    notes: '⚠️ Una volta scelto il modello e indicizzata la KB, NON puoi cambiarlo senza re-indicizzare TUTTO (la dimensione vettoriale deve coincidere). Se imposti Gemini, qui sotto compare il dropdown del modello chat Gemini per coerenza nello spazio semantico.',
  },
};

const SERVICE_ITEMS: ServiceItem[] = [
  // ─────────────────────────────────────────────────────────────────────────
  // INGESTION — caricamento Knowledge Base
  // ─────────────────────────────────────────────────────────────────────────
  EMBEDDINGS_ITEM,
  {
    key: 'document_parsing',
    label: 'Parser Documenti Acquisto (DDT/Fatture/Ordini)',
    desc: 'Estrazione AI strutturata da documenti di acquisto — PDF, immagini, Excel (.xlsx/.xls), Word (.docx), CSV e HTML: header (numero, data, fornitore) + righe (SKU, quantità, prezzo).',
    icon: ScanSearch,
    category: 'llm',
    group: 'ingestion',
    canDisable: true,
    recommendation: 'Claude e Gemini ricevono il PDF in formato nativo (best per layout complessi e tabelle multipagina). OpenAI usa Vision con conversione PDF→immagini. Se disattivato, ricade su OCR + regex (qualità inferiore).',
    longDescription: {
      whatItDoes: 'Riceve un documento di acquisto (PDF, immagine, Excel, Word, CSV o HTML) e restituisce un payload strutturato con header e righe. PDF e immagini vanno al modello in modalità vision; per Excel/Word/CSV/HTML il testo viene estratto e inviato allo stesso modello in modalità testo. Le righe vengono poi matchate sul catalogo per EAN/SKU esatto e, in fallback, per similarità semantica via embeddings.',
      whenItRuns: 'Ad ogni upload nel modale "Da OCR" del DDT passivo, fatture passive e ordini fornitore. Sincrono (max 3 minuti), risposta usata per pre-compilare il form.',
      impact: 'Sostituisce la pipeline OCR + regex con estrazione AI strutturata. Qualità match righe nettamente superiore. Se non configurato cade automaticamente su OCR + regex.',
      notes: 'Modello specifico in Master → Platform Models → service_key=document_parsing. Embeddings prodotti generati on-demand al create/update.',
    },
  },
  {
    key: 'ocr',
    label: 'Ingestione Knowledge Base',
    desc: 'Estrazione testo da PDF scansionati e immagini tramite Vision LLM. Necessario per indicizzare documenti cartacei.',
    icon: ScanSearch,
    category: 'llm',
    group: 'ingestion',
    canDisable: true,
    recommendation: 'GPT-4o Vision (OpenAI) o Claude PDF nativo. Selezionando Gemini si attiva il bypass: l\'OCR è gestito direttamente nella pipeline multimodale durante la query (no step OCR separato). "Disattivato" blocca completamente l\'indicizzazione di scan/immagini.',
    longDescription: {
      whatItDoes: 'Estrae testo da PDF scansionati (pagine come immagini), JPG/PNG e altri file immagine usando un modello Vision LLM. Il testo estratto viene poi chunkizzato e indicizzato nel RAG come un normale documento.',
      whenItRuns: 'Asincrono al caricamento di un nuovo file. Se il file è un PDF nativo (testo selezionabile) l\'OCR viene saltato e si usa l\'estrazione testuale diretta.',
      impact: 'Senza OCR i PDF scansionati vengono marcati no_provider e ESCLUSI dal RAG. Attivarlo è essenziale per documenti scannerizzati, fatture cartacee, contratti firmati.',
      notes: 'Con embedder=Gemini il bypass è automatico: l\'OCR avviene inline nella pipeline multimodale (no chiamata OCR separata). Per gli altri provider va configurato esplicitamente qui.',
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // RETRIEVAL & REASONING — query utente all'agente RAG
  // ─────────────────────────────────────────────────────────────────────────
  {
    key: 'rag_decomposer',
    label: '1. Decomposer (spezza query complesse)',
    desc: 'Spezza domande multi-parte in sub-query indipendenti per coprire meglio il bisogno reale.',
    icon: Brain,
    category: 'llm',
    group: 'retrieval',
    core: true,
    recommendation: 'Claude Sonnet 4.5 preferito (reasoning superiore, BFCL 70.36%). Modelli reasoning (o-series, Gemini 3 thinkingLevel HIGH) ideali. GPT-5 strict mode ottimo per structured outputs.',
    longDescription: {
      whatItDoes: 'Quando l\'utente chiede "Quanto costa il piano Pro e quali features ha rispetto al Free?" il decomposer la spezza in ["prezzo piano Pro", "differenze Pro vs Free"]. Ognuna viene cercata separatamente nella KB e i risultati vengono fusi.',
      whenItRuns: 'Solo nella pipeline agentic e SOLO se la query è giudicata complessa da `isComplexQuery()` (presenza di "e", "rispetto a", multiple frasi). Per query semplici/atomiche viene saltato.',
      impact: 'Migliora drasticamente la copertura su domande composite (recall significativamente più alto). Costo: 1 chiamata LLM piccola solo quando attivo (~100-300ms).',
      notes: 'Usa il modello chat della pipeline RAG scelto nel passo 1, sotto l\'embedder: si cambia lì.',
    },
  },
  {
    key: 'rag_reranker',
    label: '2. Reranker (riordina i chunk recuperati)',
    desc: "Dopo il retrieval iniziale, riordina i chunk per pertinenza reale. È il salto di qualità più grande della pipeline RAG.",
    icon: Brain,
    category: 'llm',
    group: 'retrieval',
    core: true,
    // 'none' = reranking disattivato. La select è la sola sorgente del
    // provider primario: salva la modalità rerank (vedi handleProviderChange).
    canDisable: true,
    recommendation: 'Modelli veloci e a basso costo: GPT-4o-mini strict mode, Gemini 2.5 Flash-Lite. Il "Cross-check" (vedi sotto) lancia due provider in parallelo e fonde i risultati.',
    longDescription: {
      whatItDoes: 'Dopo il retrieval iniziale rapido (BM25 + embeddings, ~50 chunk recuperati), un secondo modello specializzato valuta la rilevanza fine di ogni chunk rispetto alla query e li RI-ORDINA. Tiene solo i top-N più pertinenti.',
      whenItRuns: 'Subito dopo il retrieval, prima della sintesi. Sempre attivo se almeno un provider è disponibile (vedi "Modalità Reranking" per scegliere singolo o ibrido).',
      impact: 'È il salto di qualità più grande della pipeline RAG. Risposte molto più centrate, meno rumore, meno allucinazioni. Costo: +200-500ms per query.',
      notes: 'Modalità "Ibrido" → massima qualità con Reciprocal Rank Fusion.',
    },
  },
  {
    key: 'rag_synthesis',
    label: '3. Synthesis (genera risposta finale dai chunk)',
    desc: "Trasforma i chunk top-N rerankati in una risposta sintetica con citazioni e confidence score.",
    icon: Brain,
    category: 'llm',
    group: 'retrieval',
    core: true,
    recommendation: 'Claude Sonnet 4.5 con prompt caching per qualità/costo ottimale. GPT-5 strict mode per structured outputs e multimodal. Gemini 3 Pro per context > 100k o Google Search nativa.',
    longDescription: {
      whatItDoes: 'Prende i chunk top-N rerankati e li trasforma in una risposta sintetica strutturata con citazioni delle fonti e confidence score. È il "secondo LLM" che lavora a monte dell\'agente per pre-masticare i dati RAG.',
      whenItRuns: 'Per ogni domanda utente, SOLO se l\'agente ha "Sintesi Nativa = OFF" (modalità pre_synthesized). Se Sintesi Nativa è ON, questo servizio viene bypassato e l\'agente riceve i chunk grezzi.',
      impact: 'Migliora notevolmente la qualità delle risposte (citazioni tracciate, meno allucinazioni) ma aggiunge +200-800ms e una chiamata LLM extra. Consigliato per chatbot/assistant; sconsigliato per voice agent dove la latenza è critica.',
      notes: 'Usa il modello chat della pipeline RAG scelto nel passo 1, sotto l\'embedder: si cambia lì. Se fallisce, l\'agente riceve i chunk grezzi come fallback (status SKIP) — la pipeline non si rompe.',
    },
  },
  {
    // Slot dedicato per la sintesi multimodale (chunk con
    // modality=image). Selezionabile separatamente da rag_synthesis text-only.
    // Backend `setPlatformModel('rag_synthesis_multimodal')` rifiuta a 400 i
    // modelli non vision-capable (assertVisionCapableForServiceKey).
    key: 'rag_synthesis_multimodal',
    label: '3b. Synthesis Multimodale (chunk con immagini)',
    desc: 'Slot dedicato per chunk con modality=image: forza un modello vision-capable e inietta i binari nativamente nel provider (Gemini inlineData / OpenAI image_url / Claude image base64). Toggle ON/OFF + scelta Primario; il Fallback (riga sotto) è opzionale.',
    icon: Brain,
    category: 'llm',
    group: 'retrieval',
    recommendation: 'Primario consigliato: Gemini 2.5 Flash (multimodal nativo, supporta anche PDF/audio/video). Fallback consigliato: un provider diverso dal primario tra Claude Sonnet 4.5 vision o GPT-5 vision. Solo modelli con modality includes "image" nel catalog sono accettati: gli altri vengono bloccati a 400 al salvataggio.',
    longDescription: {
      whatItDoes: 'Quando un chunk recuperato dal RAG ha modality=image (es. pagine PDF rasterizzate, screenshot wiki, allegati), questa slot decide chi sintetizza la risposta finale guardando DAVVERO le immagini, non solo le citazioni testuali. Scegli il provider+modello PRIMARIO; opzionalmente un FALLBACK (provider diverso, vision-capable) che subentra se il primario fallisce. Il body inviato al provider include i binari nel formato nativo (no data-URL fake, no OCR upstream).',
      whenItRuns: 'Solo se almeno un chunk recuperato ha modality=image AND R2 storage è configurato. Per query 100% testuali viene bypassato e la sintesi normale (3.) gestisce tutto.',
      impact: 'Differenza notevole su domande "cosa mostra la figura X", "leggi il timbro", "estrai la tabella dalla pagina N". Costo aggiuntivo: 1 chiamata vision (latenza tipica 1-3s, dipende dal numero di binari).',
      notes: 'Comportamento chain: (a) se hai dichiarato un FALLBACK → catena deterministica [Primario → Fallback → text-only degrade], nessun cross-provider opportunistico; (b) se il fallback è OFF → chain auto legacy [primario → altri vision provider in catalog → text-only]. Limiti hard payload (max 8 binari, max 20MB totali) configurabili in extra_config.',
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // VOICE & CONVERSATION
  // `voice_agents` (placeholder informativo) rimosso: sostituito da
  // banner in cima al gruppo. STT consolidato in 3 sotto-righe (stt_realtime/
  // stt_voicemail/stt_video) renderizzate inline da renderGroupSection.
  // ─────────────────────────────────────────────────────────────────────────
  {
    key: 'tts',
    label: 'Text-to-Speech (annunci IVR)',
    desc: 'Solo annunci IVR FreeSWITCH (prompt code, musica di attesa narrata, dialplan). Non agenti voce live.',
    icon: Volume2,
    category: 'media',
    group: 'voice',
    recommendation: 'Controlla SOLO il TTS degli annunci IVR FreeSWITCH. NON ha effetto sugli agenti voce: OpenAI Realtime, Gemini Live ed ElevenLabs usano la propria pipeline audio nativa.',
    longDescription: {
      whatItDoes: 'Genera audio (.wav) da testo per gli annunci preregistrati dell\'IVR FreeSWITCH: messaggi di benvenuto, codici prompt, musica di attesa narrata, conferme di selezione.',
      whenItRuns: 'A richiesta quando viene salvato/aggiornato un annuncio IVR. I file audio vengono cachati: il TTS non gira ad ogni chiamata, solo alla creazione.',
      impact: 'Sostituisce la necessità di registrare voci umane per gli annunci di sistema. Voci OpenAI multilingua di alta qualità.',
      notes: '🚫 NON impatta gli agenti voce live. Ognuno ha la propria pipeline TTS nativa che si configura sul singolo agente.',
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ANALYTICS & POST-PROCESSING
  // ─────────────────────────────────────────────────────────────────────────
  {
    key: 'conversation_analysis',
    label: 'Analisi Conversazioni',
    desc: 'Sentiment, riassunti, intent e key moments estratti automaticamente dopo la fine di chiamate/chat.',
    icon: BarChart3,
    category: 'llm',
    group: 'analytics',
    recommendation: 'Claude Sonnet 4.5 (BFCL 70.36% vs GPT-5 59.22%). GPT-5 strict mode ottimo per multimodal/structured. Gemini 3 Pro per trascrizioni molto lunghe (context 2M). Per grandi volumi storici considera Claude Message Batches API (-50% costo, SLA 24h).',
    longDescription: {
      whatItDoes: 'Analizza la trascrizione completa di una chiamata o chat dopo la chiusura. Estrae: sentiment_score (-1 a +1) + label, riassunto in 2-3 frasi, customer_intent, topics principali, key_moments (max 3 momenti emotivi con timestamp).',
      whenItRuns: 'Asincrono DOPO la fine di una conversazione, oppure tramite job batch storico. Non impatta la latenza dell\'interazione live.',
      impact: 'Dati scritti nella scheda conversazione/chiamata e nelle dashboard analytics. Servono a misurare qualità servizio e identificare topic ricorrenti.',
      notes: 'L\'attivazione effettiva dipende dai flag dell\'agente (sentiment_enabled, transcription_enabled) e dal flusso che la innesca (chatbot, voice, batch).',
    },
  },
];

interface EmbedderChatModelSelectProps {
  provider: 'openai' | 'gemini';
  value: string;
  saving: boolean;
  onChange: (model: string) => void;
}

/**
 * Modello chat coerente con l'embedder: opzioni SOLO dal catalogo modelli (non
 * deprecati). Senza catalogo la select non inventa modelli: mostra la scelta
 * salvata e segnala che il catalogo non è disponibile.
 */
function EmbedderChatModelSelect({ provider, value, saving, onChange }: EmbedderChatModelSelectProps) {
  const { data, isLoading, isError, refetch } = useChatModelOptions(provider);
  const options = (data?.models ?? [])
    .filter((m) => m.status !== 'deprecated')
    .map((m) => ({ id: m.id, label: m.isRecommended ? `${m.displayName || m.id} (consigliato)` : (m.displayName || m.id) }));
  const catalogUnavailable = !isLoading && (isError || options.length === 0);
  const withCurrent = value && !options.some((o) => o.id === value)
    ? [{ id: value, label: `${value} (attuale)` }, ...options]
    : options;
  return (
    <div className="space-y-1">
      <Select value={value || undefined} onValueChange={onChange} disabled={saving || isLoading || catalogUnavailable}>
        <SelectTrigger className="w-full h-7 text-xs" data-testid={`${provider}-chat-model-select`}>
          {saving || isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <SelectValue placeholder="Seleziona il modello chat…" />}
        </SelectTrigger>
        <SelectContent>
          {withCurrent.map((o) => (
            <SelectItem key={o.id} value={o.id} className="text-xs">{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {catalogUnavailable && (
        <p className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400" role="status" data-testid={`${provider}-chat-model-catalog-unavailable`}>
          <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span>Catalogo modelli non disponibile.</span>
          <Button variant="link" size="sm" className="h-auto p-0 text-[11px]" onClick={() => { void refetch(); }}>Riprova</Button>
        </p>
      )}
    </div>
  );
}

interface StepHeadingProps {
  /** Numero del passo; assente per le sezioni di supporto. */
  step?: number;
  title: string;
  subtitle: string;
}

/** Titolo di sezione della pagina, con numero del passo quando è un passo da compiere. */
function StepHeading({ step, title, subtitle }: StepHeadingProps) {
  return (
    <div className="flex items-start gap-2">
      {step !== undefined && (
        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
          {step}
        </span>
      )}
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground leading-relaxed">{subtitle}</p>
      </div>
    </div>
  );
}

// Schema serializzato dal backend: 'disabled' | '<provider>' | 'hybrid:<a>+<b>'.
// Derivato dal routing dei due leg (rag_rerank / rag_rerank_secondary), mai
// salvato a parte. Tipizzato come `string` perché la combinatoria è dinamica.
type RerankMode = string;
type RerankLegServiceKey = 'rag_rerank' | 'rag_rerank_secondary';
type RerankProviderId = 'openai' | 'claude' | 'google';
const RERANK_PROVIDER_IDS: ReadonlyArray<RerankProviderId> = ['openai', 'claude', 'google'];
const RERANK_PROVIDER_LABEL: Record<RerankProviderId, string> = {
  openai: 'OpenAI',
  claude: 'Claude',
  google: 'Gemini',
};
interface ParsedRerankMode {
  kind: 'disabled' | 'single' | 'hybrid';
  primary: RerankProviderId | null;
  secondary: RerankProviderId | null;
}
function isRerankProviderId(v: string | null | undefined): v is RerankProviderId {
  return v === 'openai' || v === 'claude' || v === 'google';
}
function parseRerankRequestedMode(raw: string | null | undefined): ParsedRerankMode {
  if (!raw || raw === 'disabled') return { kind: 'disabled', primary: null, secondary: null };
  const m = /^hybrid:(openai|claude|google)\+(openai|claude|google)$/.exec(raw);
  if (m && isRerankProviderId(m[1]) && isRerankProviderId(m[2])) {
    // Parità con backend `parseRerankMode`: hybrid con a===b è degenere → single.
    if (m[1] === m[2]) return { kind: 'single', primary: m[1], secondary: null };
    return { kind: 'hybrid', primary: m[1], secondary: m[2] };
  }
  if (isRerankProviderId(raw)) return { kind: 'single', primary: raw, secondary: null };
  return { kind: 'disabled', primary: null, secondary: null };
}

// Count chunk multimodali tenant per Synthesis MM auto-suggest.
interface MultimodalChunksCount {
  image_chunks: number;
  audio_chunks: number;
  video_chunks: number;
  total_multimodal: number;
}

function describeRerankMode(parsed: ParsedRerankMode): string {
  if (parsed.kind === 'disabled') {
    return 'Disattivato — i risultati useranno l\'ordine originale del retrieval.';
  }
  if (parsed.kind === 'single' && parsed.primary) {
    return `Modello singolo: ${RERANK_PROVIDER_LABEL[parsed.primary]}. Latenza ~300 ms, 1 chiamata LLM.`;
  }
  if (parsed.kind === 'hybrid' && parsed.primary && parsed.secondary) {
    return `Cross-check attivo: ${RERANK_PROVIDER_LABEL[parsed.primary]} + ${RERANK_PROVIDER_LABEL[parsed.secondary]} in parallelo, fusione RRF.`;
  }
  return 'Configurazione non riconosciuta.';
}

export default function ServiceRoutingTab() {
  const { user } = useAuth();
  return <ScopedServiceRoutingTab key={`${user?.tenantId ?? ''}:${user?.id ?? ''}`} />;
}

function ScopedServiceRoutingTab() {
  const [governanceRevision, setGovernanceRevision] = useState<number | undefined>();
  const [routingRevisions, setRoutingRevisions] = useState<Record<string, number>>({});
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [data, setData] = useState<AiProviderStatusResponse | null>(null);
  const [rerankMode, setRerankMode] = useState<RerankModeStatus | null>(null);
  // Counts chunk MM per banner auto-suggest sintesi multimodale.
  const [multimodalCounts, setMultimodalCounts] = useState<MultimodalChunksCount | null>(null);
  // Boost multimodale per i reranker text-only (OpenAI/Claude).
  // Range 0.5..2.0, default 1.0. Persistito in platform_model_config.extra_config.
  const [rerankBoost, setRerankBoost] = useState<number>(1.0);
  const [rerankBoostDraft, setRerankBoostDraft] = useState<string>('1.0');
  const [savingBoost, setSavingBoost] = useState<boolean>(false);
  const [ocrStatus, setOcrStatus] = useState<OcrStatusState | null>(null);
  const [ocrCoherence, setOcrCoherence] = useState<OcrCoherenceState | null>(null);
  const [ocrTimeoutMin, setOcrTimeoutMin] = useState<number>(30);
  const [ocrTimeoutDraft, setOcrTimeoutDraft] = useState<string>('30');
  const [ocrFailures, setOcrFailures] = useState<OcrFailureEntry[]>([]);
  const [ocrFailuresTotal, setOcrFailuresTotal] = useState<number>(0);

  // Suggerimenti dinamici real-time per pipeline RAG (synthesis/decomposer/reranker)
  // basati su API keys attive del tenant + embedder corrente. Sovrascrivono `recommendation` statico.
  const [dynamicRecs, setDynamicRecs] = useState<DynamicRecs | null>(null);
  const [embedderProvider, setEmbedderProvider] = useState<'openai' | 'gemini' | null>(null);
  // Metadati di lock dell'embedder (provider/model/locked_at)
  // per il render della info-card read-only sulla riga `embeddings`.
  const [embedderModel, setEmbedderModel] = useState<string | null>(null);
  const [embedderLockedAt, setEmbedderLockedAt] = useState<string | null>(null);
  // `degraded` esposto da `/tenant/embedder-status` (campo
  // calcolato server-side: provider lockato ma API key del provider mancante).
  // Sorgente unica per il warning ambra non bloccante.
  const [embedderDegraded, setEmbedderDegraded] = useState<boolean>(false);
  const [geminiChatModel, setGeminiChatModel] = useState<string>('');
  const [savingGeminiChat, setSavingGeminiChat] = useState(false);
  // Leva chat-model per-tenant speculare per embedder=openai.
  const [openaiChatModel, setOpenaiChatModel] = useState<string>('');
  const [savingOpenaiChat, setSavingOpenaiChat] = useState(false);
  // P4 — modello chat coerente con l'embedder, validato server-side contro il
  // catalog (capability=chat, non deprecato). Sorgente per l'auto-popolamento
  // dello slot obbligatorio quando è ancora vuoto.
  const [coherentChatModel, setCoherentChatModel] = useState<{ provider: 'gemini' | 'openai'; model: string } | null>(null);
  // Guard anti-overwrite: l'auto-popolamento parte SOLO dopo che
  // /tenant/embedder-status ha caricato i valori salvati. Senza questo, uno slot
  // ancora '' perché lo status non è ancora arrivato (le recommendations possono
  // risolversi prima) verrebbe scambiato per "vuoto" e sovrascriverebbe la
  // scelta esistente del tenant.
  const [statusLoaded, setStatusLoaded] = useState<boolean>(false);

  // Custom AI first-class nelle status card header.
  // Indipendente da `activeProviders` (che riflette solo i canonical),
  // mostra stato vero (kind, include_in_routing, health_status) per ciascun
  // endpoint custom del tenant.
  type CustomEndpointHeader = {
    id: string;
    name: string;
    kind: string;
    include_in_routing: boolean;
    health_status: 'unknown' | 'ok' | 'degraded' | 'down';
  };
  const [customEndpoints, setCustomEndpoints] = useState<ReadonlyArray<CustomEndpointHeader>>([]);
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ endpoints?: CustomEndpointHeader[] }>('/custom-ai/endpoints')
      .then((r) => {
        const list = r.data?.endpoints;
        if (!cancelled && Array.isArray(list)) setCustomEndpoints(list);
      })
      .catch(() => { /* fail-soft: nessuna card custom */ });
    return () => { cancelled = true; };
  }, []);

  const queryClient = useQueryClient();

  // Unica sorgente per le select provider: payload
  // `/ai-tools/available-providers?service_key=<key>` per riga (capability
  // filter via `reason_code` + endpoint custom del tenant). Finché non è
  // arrivato la select è in caricamento; su errore mostra motivo + retry.
  // Nessun ripiego client-side: una lista nel FE non conosce né le capability
  // né gli endpoint custom e proporrebbe provider che il backend rifiuta.
  const [providerAvailabilityByKey, setProviderAvailabilityByKey] = useState<Record<string, ProviderAvailability>>({});
  const availabilityFor = useCallback(
    (uiKey: string): ProviderAvailability => providerAvailabilityByKey[uiKey] ?? { status: 'loading' },
    [providerAvailabilityByKey],
  );

  // Provider effettivi del tenant per le funzioni voce (TTS IVR e le 3 righe
  // STT), non esposti da /ai-tools/provider-status: riga del tenant sopra il
  // default di piattaforma, letti da /ai-tools/service-models.
  const [routedProviders, setRoutedProviders] = useState<Record<string, string>>({});
  // Funzioni con una scelta esplicita del tenant (non dal preset): per le
  // funzioni RAG la scelta vince sul modello chat della pipeline.
  const [explicitServiceKeys, setExplicitServiceKeys] = useState<ReadonlySet<string>>(new Set());
  // Righe RAG che l'utente sta personalizzando (selettori visibili prima del salvataggio).
  const [customizingKeys, setCustomizingKeys] = useState<ReadonlySet<string>>(new Set());
  const fetchRoutedProviders = useCallback(async () => {
    try {
      const res = await apiClient.get<Array<{ service_key: string; provider: string; explicit?: boolean; governance_revision: number }>>('/ai-tools/service-models');
      const list = res.data;
      if (!Array.isArray(list)) return;
      const next: Record<string, string> = {};
      const explicit = new Set<string>();
      for (const r of list) {
        if (VOICE_ROUTED_KEYS.has(r.service_key)) next[r.service_key] = r.provider || 'none';
        if (r.explicit) explicit.add(r.service_key);
      }
      setRoutedProviders(next);
      setRoutingRevisions(Object.fromEntries(list.map(row => [row.service_key, row.governance_revision])));
      setExplicitServiceKeys(explicit);
    } catch {
      /* fail-soft: le righe voce mostrano OpenAI come default */
    }
  }, []);

  const fetchOcrCoherence = useCallback(async () => {
    try {
      const res = await apiClient.get<{ data?: OcrCoherenceState }>('/ai-tools/ocr-coherence-status');
      const raw = (res?.data?.data ?? res?.data ?? null) as OcrCoherenceState | null;
      setOcrCoherence(raw);
    } catch {
      setOcrCoherence(null);
    }
  }, []);

  const fetchOcrTimeout = useCallback(async () => {
    try {
      const res = await apiClient.get<{ data?: { timeout_max_minutes?: number }; timeout_max_minutes?: number }>('/ai-tools/ocr-timeout');
      const raw = res?.data?.data ?? res?.data ?? null;
      const m = Number(raw?.timeout_max_minutes) || 30;
      setOcrTimeoutMin(m);
      setOcrTimeoutDraft(String(m));
    } catch {
      setOcrTimeoutMin(30);
      setOcrTimeoutDraft('30');
    }
  }, []);

  const fetchOcrFailures = useCallback(async () => {
    try {
      const res = await apiClient.get<{ data?: { failures?: OcrFailureEntry[]; total_recent?: number }; failures?: OcrFailureEntry[]; total_recent?: number }>('/ai-tools/ocr-recent-failures?limit=5');
      const raw = res?.data?.data ?? res?.data ?? null;
      setOcrFailures(raw?.failures || []);
      setOcrFailuresTotal(raw?.total_recent || 0);
    } catch {
      setOcrFailures([]);
      setOcrFailuresTotal(0);
    }
  }, []);

  const fetchOcrStatus = useCallback(async () => {
    try {
      const res = await apiClient.get<{ data?: OcrStatusState }>('/ai-tools/ocr-provider-status');
      const raw = (res?.data?.data ?? res?.data ?? null) as OcrStatusState | null;
      setOcrStatus(raw);
    } catch {
      setOcrStatus(null);
    }
  }, []);

  const fetchRerankMode = useCallback(async () => {
    try {
      const res = await apiClient.get<RerankModeStatus>('/ai-tools/rerank-mode');
      const parsed = RerankModeStatusSchema.safeParse(res?.data);
      setRerankMode(parsed.success ? parsed.data : null);
    } catch (err) {
      // non-blocking: don't toast on initial load
      setRerankMode(null);
    }
  }, []);

  // Fetch count chunk MM per auto-suggest banner Synthesis MM.
  const fetchMultimodalCounts = useCallback(async () => {
    try {
      const res = await apiClient.get<{ data?: MultimodalChunksCount } | MultimodalChunksCount>('/ai-tools/multimodal-chunks-count');
      const outer = res?.data as { data?: MultimodalChunksCount } | undefined;
      const raw = (outer?.data ?? res?.data ?? null) as MultimodalChunksCount | null;
      if (raw && typeof raw.image_chunks === 'number') {
        setMultimodalCounts(raw);
      }
    } catch {
      setMultimodalCounts(null);
    }
  }, []);

  // Fetch matrice provider compatibili per ciascun service_key
  // visibile (capability filter + endpoint custom tenant).
  const fetchAvailableProvidersFor = useCallback(async (uiKey: string) => {
    // L'endpoint backend valida `service_key` contro SERVICE_CAPABILITY_MAP
    // usando i canonical (es. `embedding`, `tts_ivr`, `rag_eval`, `rag_rerank`).
    // La UI però chiama con le sue key locali (`embeddings`, `tts`, …): traduco
    // qui prima dell'HTTP e salvo nello state sotto la UI key, così il lookup
    // downstream (`availabilityFor(item.key)`) continua a funzionare.
    const apiKey = PLATFORM_SERVICE_KEY_MAP[uiKey] ?? uiKey;
    const fail = (message: string): void => {
      setProviderAvailabilityByKey((prev) => ({
        ...prev,
        [uiKey]: { status: 'error', message, onRetry: () => { void fetchAvailableProvidersFor(uiKey); } },
      }));
    };
    setProviderAvailabilityByKey((prev) => (prev[uiKey]?.status === 'ready' ? prev : { ...prev, [uiKey]: { status: 'loading' } }));
    try {
      const res = await apiClient.get<{ providers?: AvailableProviderEntry[] }>(
        `/ai-tools/available-providers?service_key=${encodeURIComponent(apiKey)}`,
      );
      const providers = res.data?.providers;
      if (!Array.isArray(providers)) {
        fail('Risposta del server senza elenco provider.');
        return;
      }
      setProviderAvailabilityByKey((prev) => ({ ...prev, [uiKey]: { status: 'ready', providers } }));
    } catch (err) {
      fail(formatApiErrorMessage(err, 'Elenco provider non caricato'));
    }
  }, []);

  // Al mount + dopo ogni refresh /provider-status, popola
  // `providerAvailabilityByKey` per i service_key configurabili (i 9 SERVICE_ITEMS
  // + le 3 sotto-righe STT). Le richieste sono parallele; ognuna è fail-soft.
  useEffect(() => {
    if (!data) return;
    const keys = [
      ...SERVICE_ITEMS.map((s) => s.key),
      ...STT_SUBROWS.map((s) => s.key),
      // La riga fallback multimodale ha capability filter
      // diverso dal primario (vision-capable, escluso `currentProvider`):
      // serve un fetch dedicato sul service_key fallback.
      'rag_synthesis_multimodal_fallback',
    ];
    void Promise.all(keys.map((k) => fetchAvailableProvidersFor(k)));
  }, [data, fetchAvailableProvidersFor]);

  // Load boost multimodale rerank
  const fetchRerankBoost = useCallback(async () => {
    try {
      const res = await apiClient.get<{ data?: { value?: number }; value?: number }>('/ai-tools/rag-rerank-boost');
      const raw = res?.data?.data ?? res?.data ?? null;
      const v = Number(raw?.value);
      const clamped = Number.isFinite(v) ? Math.min(2.0, Math.max(0.5, v)) : 1.0;
      setRerankBoost(clamped);
      setRerankBoostDraft(clamped.toFixed(2));
    } catch {
      setRerankBoost(1.0);
      setRerankBoostDraft('1.00');
    }
  }, []);

  const saveRerankBoost = useCallback(async (value: number) => {
    if (!Number.isFinite(value) || value < 0.5 || value > 2.0) {
      toast({ title: 'Valore non valido', description: 'Il boost deve essere tra 0.5 e 2.0', variant: 'destructive' });
      setRerankBoostDraft(rerankBoost.toFixed(2));
      return;
    }
    setSavingBoost(true);
    try {
      await apiClient.put('/ai-tools/rag-rerank-boost', { value });
      setRerankBoost(value);
      setRerankBoostDraft(value.toFixed(2));
      toast({ title: 'Boost aggiornato', description: `rerank_multimodal_boost = ${value.toFixed(2)}` });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Errore salvataggio', description: msg, variant: 'destructive' });
      setRerankBoostDraft(rerankBoost.toFixed(2));
    } finally {
      setSavingBoost(false);
    }
  }, [rerankBoost, toast]);

  // Il primo caricamento mostra lo spinner a pagina intera; i ricaricamenti
  // successivi ("Aggiorna", salvataggi) aggiornano i dati senza smontare la
  // pagina, così sezioni aperte e scroll restano dove sono.
  const loadedOnceRef = useRef(false);
  const fetchStatus = useCallback(async () => {
    if (loadedOnceRef.current) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await apiClient.get<unknown>('/ai-tools/provider-status');
      const parsed = AiProviderStatusResponseSchema.safeParse(res.data);
      setData(parsed.success ? parsed.data : null);
    } catch (err) {
      toast({ title: 'Errore', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      loadedOnceRef.current = true;
      setLoading(false);
    }
    try {
      await Promise.all([fetchRerankMode(), fetchRerankBoost(), fetchMultimodalCounts(), fetchRoutedProviders()]);
    } finally {
      setRefreshing(false);
    }
  }, [toast, fetchRerankMode, fetchRerankBoost, fetchMultimodalCounts, fetchRoutedProviders]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Fetch suggerimenti dinamici + embedder corrente all'avvio
  const fetchRagRecs = useCallback(async () => {
    try {
      const res = await apiClient.get<{
        services?: DynamicRecs;
        embedder_provider?: 'openai' | 'gemini' | null;
        embedder_coherent_chat_model?: { provider: 'gemini' | 'openai'; model: string } | null;
      }>('/tenant/rag-pipeline-recommendations');
      // L'endpoint può rispondere con envelope singolo (res.data) o doppio (res.data.data).
      const outer = res?.data as (typeof res)['data'] & { data?: (typeof res)['data'] } | undefined;
      const payload = outer?.data || outer;
      if (payload?.services) setDynamicRecs(payload.services);
      if (payload?.embedder_provider !== undefined) setEmbedderProvider(payload.embedder_provider);
      if (payload?.embedder_coherent_chat_model !== undefined) setCoherentChatModel(payload.embedder_coherent_chat_model);
    } catch {
      setDynamicRecs(null);
    }
  }, []);
  const fetchEmbedderStatus = useCallback(async () => {
    try {
      const res = await apiClient.get<{
        governance_revision?: number;
        gemini_chat_model?: string;
        openai_chat_model?: string;
        current?: 'openai' | 'gemini' | null;
        degraded?: boolean;
        model?: string | null;
        locked_at?: string | null;
      }>('/tenant/embedder-status');
      const outer = res?.data as (typeof res)['data'] & { data?: (typeof res)['data'] } | undefined;
      const payload = outer?.data || outer;
      if (payload?.gemini_chat_model) setGeminiChatModel(payload.gemini_chat_model);
      setGovernanceRevision(payload?.governance_revision);
      if (payload?.openai_chat_model) setOpenaiChatModel(payload.openai_chat_model);
      // `current` è null finché il tenant non sceglie l'embedder.
      // Allinea `embedderProvider` a /tenant/embedder-status oltre che a
      // /rag-pipeline-recommendations: garantisce che, se l'utente esce dal
      // gate dal dropdown della riga `embeddings`, lo stato locale rifletta
      // immediatamente la scelta senza attendere il prossimo polling delle
      // recommendations.
      if (payload?.current === 'openai' || payload?.current === 'gemini') {
        setEmbedderProvider(payload.current);
      } else if (payload?.current === null || payload?.current === undefined) {
        setEmbedderProvider(null);
      }
      setEmbedderDegraded(!!payload?.degraded);
      // Propaga metadati lock per info-card read-only.
      setEmbedderModel(typeof payload?.model === 'string' ? payload.model : null);
      setEmbedderLockedAt(typeof payload?.locked_at === 'string' ? payload.locked_at : null);
      setStatusLoaded(true);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { fetchRagRecs(); fetchEmbedderStatus(); }, [fetchRagRecs, fetchEmbedderStatus]);

  // Gate "embedder-first": la scelta avviene SOLO nel popup globale
  // EmbedderChoiceWizard (conferma esplicita + configurazione consigliata).
  // Il pulsante della pagina lo riapre con lo stesso meccanismo dell'avviso in
  // header; quando il popup blocca l'embedder, lo stato condiviso
  // (useEmbedderGate) diventa `is_locked` e la pagina ricarica i suoi dati.
  const { status: embedderGateStatus } = useEmbedderGate();
  const openEmbedderWizard = useCallback(() => {
    try {
      window.sessionStorage.removeItem('embedder-wizard-dismissed');
    } catch {
      /* sessionStorage non disponibile: il wizard si riapre comunque via evento */
    }
    window.dispatchEvent(new CustomEvent('embedder-wizard:reopen'));
  }, []);
  const refreshAfterEmbedderLock = useCallback(() => {
    void fetchRagRecs();
    void fetchEmbedderStatus();
    void fetchStatus();
    void queryClient.invalidateQueries({ queryKey: ['rag-pipeline-recommendations'] });
    void queryClient.invalidateQueries({ queryKey: ['platform-models'] });
  }, [fetchRagRecs, fetchEmbedderStatus, fetchStatus, queryClient]);
  const lockSyncedRef = useRef(false);
  useEffect(() => {
    if (!statusLoaded || embedderProvider !== null || lockSyncedRef.current) return;
    if (embedderGateStatus?.is_locked === true) {
      lockSyncedRef.current = true;
      refreshAfterEmbedderLock();
    }
  }, [statusLoaded, embedderProvider, embedderGateStatus, refreshAfterEmbedderLock]);

  const handleSaveGeminiChatModel = useCallback(async (model: string) => {
    setSavingGeminiChat(true);
    try {
      await apiClient.put('/tenant/embedder-status', { gemini_chat_model: model || null, expected_revision: governanceRevision });
      await fetchEmbedderStatus();
      setGeminiChatModel(model);
      toast({ title: 'Modello chat Gemini aggiornato', description: model || '(default)' });
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({ title: 'Errore', description: __err?.message || 'salvataggio fallito', variant: 'destructive' });
    } finally {
      setSavingGeminiChat(false);
    }
  }, [toast, governanceRevision, fetchEmbedderStatus]);

  // Save speculare leva chat-model OpenAI (embedder=openai).
  const handleSaveOpenaiChatModel = useCallback(async (model: string) => {
    setSavingOpenaiChat(true);
    try {
      await apiClient.put('/tenant/embedder-status', { openai_chat_model: model || null, expected_revision: governanceRevision });
      await fetchEmbedderStatus();
      setOpenaiChatModel(model);
      toast({ title: 'Modello chat OpenAI aggiornato', description: model || '(default)' });
      void fetchOcrCoherence();
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({ title: 'Errore', description: __err?.message || 'salvataggio fallito', variant: 'destructive' });
    } finally {
      setSavingOpenaiChat(false);
    }
  }, [toast, fetchOcrCoherence, governanceRevision, fetchEmbedderStatus]);

  // P4 — auto-popolamento slot chat-model obbligatorio: quando l'embedder è
  // scelto e lo slot coerente è ancora VUOTO, persiste il modello consigliato
  // (già validato server-side contro il catalog). Si attiva al massimo una volta
  // per provider (ref-guard) e non sovrascrive MAI una scelta esistente.
  const autoFilledChatRef = useRef<{ gemini: boolean; openai: boolean }>({ gemini: false, openai: false });
  useEffect(() => {
    if (!coherentChatModel || !statusLoaded) return;
    if (embedderProvider === 'gemini' && coherentChatModel.provider === 'gemini'
        && geminiChatModel === '' && !autoFilledChatRef.current.gemini && !savingGeminiChat) {
      autoFilledChatRef.current.gemini = true;
      void handleSaveGeminiChatModel(coherentChatModel.model);
    } else if (embedderProvider === 'openai' && coherentChatModel.provider === 'openai'
        && openaiChatModel === '' && !autoFilledChatRef.current.openai && !savingOpenaiChat) {
      autoFilledChatRef.current.openai = true;
      void handleSaveOpenaiChatModel(coherentChatModel.model);
    }
  }, [coherentChatModel, statusLoaded, embedderProvider, geminiChatModel, openaiChatModel, savingGeminiChat, savingOpenaiChat, handleSaveGeminiChatModel, handleSaveOpenaiChatModel]);

  useEffect(() => {
    const ocrProv = data?.services?.ocr;
    if (ocrProv && ocrProv !== 'none') {
      fetchOcrStatus();
    } else {
      setOcrStatus(null);
    }
    fetchOcrCoherence();
    fetchOcrTimeout();
    fetchOcrFailures();
  }, [data?.services?.ocr, fetchOcrStatus, fetchOcrCoherence, fetchOcrTimeout, fetchOcrFailures]);

  // Poll OCR failures every 30s so admins see new errors without reloading
  // the page. Cleared on unmount and on dependency change.
  useEffect(() => {
    const id = setInterval(() => { fetchOcrFailures(); }, 30_000);
    return () => clearInterval(id);
  }, [fetchOcrFailures]);

  const handleSaveOcrTimeout = useCallback(async () => {
    const m = Number(ocrTimeoutDraft);
    if (!isFinite(m) || m < 5 || m > 60) {
      toast({ title: 'Valore non valido', description: 'Inserisci un numero tra 5 e 60 minuti', variant: 'destructive' });
      return;
    }
    setSaving('ocr_timeout');
    try {
      await apiClient.put('/ai-tools/ocr-timeout', { minutes: m });
      await fetchOcrTimeout();
      toast({ title: 'Timeout OCR aggiornato', description: `${m} minuti` });
    } catch (err) {
      toast({ title: 'Errore', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  }, [ocrTimeoutDraft, fetchOcrTimeout, toast]);

  // La modalità rerank è derivata dalle righe di routing dei due leg: ogni
  // modifica è un `PUT /ai-tools/service-provider` su `rag_rerank` (primario,
  // `none` = reranking spento) o `rag_rerank_secondary` (cross-check, `none` =
  // spento). Dopo il salvataggio si rilegge la modalità derivata dal backend.
  const saveRerankLegs = useCallback(async (
    legs: ReadonlyArray<{ service_key: RerankLegServiceKey; provider: RerankProviderId | 'none' }>,
  ) => {
    setSaving('rerank_mode');
    try {
      for (const leg of legs) {
        await apiClient.put('/ai-tools/service-provider', leg);
      }
      // Il backend riallinea il modello al primo consigliato del nuovo
      // provider: il picker deve rileggerlo, non usare la lettura in cache.
      clearSharedPickerReads();
      await Promise.all([fetchRerankMode(), fetchStatus()]);
      toast({ title: 'Reranking aggiornato' });
    } catch (err) {
      toast({ title: 'Errore', description: formatApiErrorMessage(err, 'Aggiornamento reranking fallito'), variant: 'destructive' });
      await fetchRerankMode();
    } finally {
      setSaving(null);
    }
  }, [fetchRerankMode, fetchStatus, toast]);

  // Toggle cross-check unico. ON → secondario = primo provider rerank attivo
  // diverso dal primario (fallback: openai, o claude se il primario è già
  // openai); OFF → secondario `none` (modello singolo).
  const handleRerankCrossCheckToggle = useCallback(async (next: boolean) => {
    const parsed = parseRerankRequestedMode(rerankMode?.requested);
    if (!parsed.primary) return;
    if (!next) {
      await saveRerankLegs([{ service_key: 'rag_rerank_secondary', provider: 'none' }]);
      return;
    }
    const primary = parsed.primary;
    const activeCandidates = RERANK_PROVIDER_IDS.filter((p) => p !== primary && !!data?.[p]?.active);
    const fallbackSecondary: RerankProviderId = primary === 'openai' ? 'claude' : 'openai';
    const defaultSecondary: RerankProviderId = activeCandidates[0] ?? fallbackSecondary;
    await saveRerankLegs([{ service_key: 'rag_rerank_secondary', provider: defaultSecondary }]);
  }, [saveRerankLegs, rerankMode, data]);

  const handleRerankSecondaryProviderChange = useCallback(async (newSecondary: RerankProviderId) => {
    const parsed = parseRerankRequestedMode(rerankMode?.requested);
    if (!parsed.primary || newSecondary === parsed.primary || newSecondary === parsed.secondary) return;
    await saveRerankLegs([{ service_key: 'rag_rerank_secondary', provider: newSecondary }]);
  }, [saveRerankLegs, rerankMode]);

  const RAG_SERVICE_KEYS: Record<string, string> = useMemo(() => ({
    conversation_analysis: 'conversation_analysis',
    rag_synthesis: 'rag_synthesis',
    rag_synthesis_multimodal: 'rag_synthesis_multimodal',
    rag_synthesis_multimodal_fallback: 'rag_synthesis_multimodal_fallback',
    rag_decomposer: 'rag_decomposition',
    rag_reranker: 'rag_rerank',
    rag_rerank_secondary: 'rag_rerank_secondary',
    ocr: 'ocr',
    document_parsing: 'document_parsing',
    tts: 'tts_ivr',
    // STT consolidato: 3 service_key indipendenti su platform_model_config.
    stt_realtime: 'stt_realtime',
    stt_voicemail: 'stt_voicemail',
    stt_video: 'stt_video',
  }), []);

  // Provider corrente di una riga: le funzioni voce dal routing effettivo del
  // tenant, il reranker dalla modalità rerank (sorgente unica), le altre dalla
  // mappa di /ai-tools/provider-status.
  const currentProviderOf = useCallback((serviceKey: string): string => {
    const platformKey = RAG_SERVICE_KEYS[serviceKey] ?? serviceKey;
    if (VOICE_ROUTED_KEYS.has(platformKey)) return routedProviders[platformKey] || 'openai';
    if (serviceKey === 'rag_reranker') {
      const parsed = parseRerankRequestedMode(rerankMode?.requested);
      return parsed.primary ?? 'none';
    }
    // Sintesi e scomposizione usano il modello chat dell'embedder (getRagChatModel).
    const chatProvider = embedderChatProvider(embedderProvider, embedderChatModelOf(embedderProvider, geminiChatModel, openaiChatModel));
    if (RAG_CHAT_FOLLOWER_KEYS.has(serviceKey) && chatProvider && !explicitServiceKeys.has(platformKey)) return chatProvider;
    return data?.services?.[serviceKey] || 'none';
  }, [RAG_SERVICE_KEYS, routedProviders, rerankMode, data, embedderProvider, geminiChatModel, openaiChatModel, explicitServiceKeys]);

  // Annulla la scelta esplicita: la funzione torna a seguire la pipeline RAG.
  const handleFollowPipeline = useCallback(async (serviceKey: string) => {
    const platformKey = RAG_SERVICE_KEYS[serviceKey] ?? serviceKey;
    setSaving(serviceKey);
    try {
      await apiClient.delete(`/ai-tools/service-models/${platformKey}/choice`);
      setCustomizingKeys((prev) => { const n = new Set(prev); n.delete(serviceKey); return n; });
      toast({ title: 'Torna a seguire la pipeline RAG' });
      await fetchStatus();
    } catch (err) {
      toast({ title: 'Errore', description: formatApiErrorMessage(err, 'Operazione non riuscita'), variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  }, [RAG_SERVICE_KEYS, toast, fetchStatus]);

  const handleProviderChange = useCallback(async (serviceKey: string, newProvider: string) => {
    if (currentProviderOf(serviceKey) === newProvider) return;

    // Reranker: la riga è il leg primario. 'none' spegne il reranking, un
    // provider lo riattiva; il cross-check resta se il secondario è diverso
    // dal nuovo primario, altrimenti si spegne (vincolo a≠b).
    if (serviceKey === 'rag_reranker') {
      if (newProvider !== 'none' && !isRerankProviderId(newProvider)) return;
      const parsed = parseRerankRequestedMode(rerankMode?.requested);
      const legs: Array<{ service_key: RerankLegServiceKey; provider: RerankProviderId | 'none' }> = [
        { service_key: 'rag_rerank', provider: newProvider },
      ];
      if (parsed.secondary && parsed.secondary === newProvider) {
        legs.push({ service_key: 'rag_rerank_secondary', provider: 'none' });
      }
      await saveRerankLegs(legs);
      return;
    }

    setSaving(serviceKey);
    try {
      const platformServiceKey = RAG_SERVICE_KEYS[serviceKey];
      if (platformServiceKey) {
        await apiClient.put('/ai-tools/service-provider', { service_key: platformServiceKey, provider: newProvider, expected_revision: routingRevisions[platformServiceKey] });
        // Il backend riallinea il modello al primo consigliato del nuovo
        // provider: il picker deve rileggerlo, non usare la lettura in cache.
        clearSharedPickerReads();
      }
      await fetchStatus();
      toast({ title: 'Routing aggiornato', description: `${serviceKey} → ${PROVIDER_META[newProvider]?.label || newProvider}` });
    } catch (err) {
      // Preferisci il payload strutturato del backend
      // (`{error:{code,message_it,hint,suggested_model}}`) al messaggio generico HTTP 400.
      toast({ title: 'Errore aggiornamento provider', description: formatApiErrorMessage(err, 'Aggiornamento provider fallito'), variant: 'destructive' });
      await fetchStatus();
    } finally {
      setSaving(null);
    }
  }, [currentProviderOf, saveRerankLegs, rerankMode, fetchStatus, toast, RAG_SERVICE_KEYS, routingRevisions]);

  const activeProviders = data
    ? Object.entries(data)
        .filter(([k, v]) => k !== 'services' && (v)?.active)
        .map(([k]) => k)
    : [];

  // "N servizi" sulle card provider: conta solo le funzioni mostrate in questa
  // pagina, con il loro provider effettivo (embedder compreso).
  const providerCounts: Record<string, number> = {};
  const countedProviders = [
    ...SERVICE_ITEMS.filter((s) => s.key !== 'embeddings').map((s) => currentProviderOf(s.key)),
    ...STT_SUBROWS.map((s) => currentProviderOf(s.key)),
    embedderProvider === 'gemini' ? 'google' : embedderProvider,
  ];
  for (const p of countedProviders) {
    if (p && p !== 'none') providerCounts[p] = (providerCounts[p] || 0) + 1;
  }

  // Pannello provider: nativi ed endpoint Custom AI nello stesso formato.
  const canonicalStatus = (key: 'openai' | 'claude' | 'google'): ProviderOverviewStatus => {
    if (activeProviders.includes(key)) return 'active';
    if (key === 'openai') return 'not_configured';
    return data?.[key]?.configured ? 'disabled' : 'not_configured';
  };
  const providerEntries: ProviderOverviewEntry[] = [
    ...CANONICAL_PROVIDER_CARDS.map((c): ProviderOverviewEntry => ({
      id: c.key,
      name: PROVIDER_META[c.key]?.label ?? c.key,
      detail: 'Integrazione nativa',
      status: canonicalStatus(c.key),
      usage: providerCounts[c.key] || 0,
      icon: PROVIDER_META[c.key]?.icon ?? Brain,
      tone: c.tone,
    })),
    ...customEndpoints.map((ep): ProviderOverviewEntry => ({
      id: `custom:${ep.id}`,
      name: 'Custom AI',
      detail: customEndpointDetail(ep.name, ep.kind),
      status: !ep.include_in_routing
        ? 'excluded'
        : ep.health_status === 'ok'
          ? 'active'
          : ep.health_status === 'unknown' ? 'unverified' : ep.health_status,
      usage: providerCounts[`custom:${ep.id}`] || 0,
      icon: PROVIDER_META.custom?.icon ?? Brain,
      tone: 'violet',
    })),
  ];
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  // Raggruppamento per fase pipeline
  const groupedServices: Record<ServiceGroup, ServiceItem[]> = {
    // L'embedder è il passo 1 (card dedicata in cima), non una riga del gruppo.
    ingestion: SERVICE_ITEMS.filter(s => s.group === 'ingestion' && s.key !== 'embeddings'),
    retrieval: SERVICE_ITEMS.filter(s => s.group === 'retrieval'),
    voice: SERVICE_ITEMS.filter(s => s.group === 'voice'),
    analytics: SERVICE_ITEMS.filter(s => s.group === 'analytics'),
  };
  function renderGroupSection(groupKey: ServiceGroup) {
    const group = SERVICE_GROUPS[groupKey];
    const items = groupedServices[groupKey];
    if (items.length === 0) return null;
    const configuredCount = items.filter(s => currentProviderOf(s.key) !== 'none').length;
    return (
      <RoutingAccordion
        key={groupKey}
        icon={group.icon}
        title={group.label}
        subtitle={group.subtitle}
        accent={group.accent}
        testId={`routing-group-${groupKey}`}
        badges={(
          <>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{items.length} funzioni</Badge>
            {configuredCount > 0 && (
              <Badge className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800">{configuredCount} attive</Badge>
            )}
          </>
        )}
      >
        <div>
          {/* Banner statico in cima al gruppo Voice: ricorda che gli
              agenti vocali si configurano per-agente nella scheda Agenti AI. */}
          {groupKey === 'voice' && (
            <div
              className="flex items-start gap-2 px-4 py-2.5 bg-sky-50 dark:bg-sky-950/30 border-b border-sky-200 dark:border-sky-900 text-[11px] text-sky-900 dark:text-sky-200"
              data-testid="voice-agents-banner"
            >
              <Phone className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <p className="leading-snug">
                Gli agenti vocali (OpenAI Realtime / Gemini Live / ElevenLabs) si
                configurano per agente nella scheda{' '}
                <Link
                  to="/ai-agents"
                  className="underline font-medium hover:text-sky-700 dark:hover:text-sky-100"
                  data-testid="voice-agents-banner-link"
                >
                  Agenti AI
                </Link>
                : provider, voce e modello sono per-agente, non globali. Le righe
                qui sotto controllano solo TTS annunci IVR e Speech-to-Text
                (voicemail, video, transcript secondario Realtime).
              </p>
            </div>
          )}
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {items.map(item => {
              if (item.key === 'rag_reranker') {
                return (
                  <Fragment key={item.key}>
                    {renderServiceRow(item)}
                    {renderRerankModeRow()}
                  </Fragment>
                );
              }
              return renderServiceRow(item);
            })}
            {/* Card consolidata Speech-to-Text con 3 sotto-righe. */}
            {groupKey === 'voice' && renderSTTConsolidated()}
          </div>
        </div>
      </RoutingAccordion>
    );
  };

  // Card "Speech-to-Text" con 3 sotto-righe indipendenti
  // (stt_realtime / stt_voicemail / stt_video). Ognuna con la propria coppia
  // provider+modello salvata su platform_model_config (provider via
  // /ai-tools/service-provider, modello via ServiceModelPicker).
  function renderSTTConsolidated() {
    return (
      <div className="p-4" data-testid="stt-consolidated-card">
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg shrink-0">
            <Mic className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Speech-to-Text
              </p>
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">3 contesti</Badge>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Trascrizione asincrona di voicemail, video/meeting e transcript
              testuale degli agenti Realtime. Ogni contesto ha provider e modello
              indipendenti. 🚫 NON è l'ASR live degli agenti voce.
            </p>
          </div>
        </div>
        <div className="space-y-2 ml-1">
          {STT_SUBROWS.map((sub) => {
            const currentProvider = currentProviderOf(sub.key);
            const isSaving = saving === sub.key;
            return (
              <div
                key={sub.key}
                data-service-row={sub.key}
                data-testid={`service-row-${sub.key}`}
                className="flex flex-wrap items-start justify-between gap-3 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40"
              >
                <div className="min-w-[14rem] flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-xs font-medium text-gray-800 dark:text-gray-200">{sub.label}</p>
                  </div>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{sub.desc}</p>
                  <FunctionHelp testId={`service-details-${sub.key}`} content={sub.longDescription} />
                </div>
                {/* Alias testid legacy: la riga 'stt' (PLATFORM_SERVICE_KEY_MAP
                    legacy → 'stt_voicemail') e 'video_transcription' (→ 'stt_video') sono
                    stati assorbiti dalla card consolidata. Marker invisibili preservano
                    la continuità con eventuale automation/QA che cerca i vecchi testid. */}
                {sub.key === 'stt_voicemail' && (
                  <span hidden data-testid="service-row-stt" aria-hidden="true" />
                )}
                {sub.key === 'stt_video' && (
                  <span hidden data-testid="service-row-video_transcription" aria-hidden="true" />
                )}
                <div className="flex flex-wrap items-center gap-2 max-w-full">
                  <ServiceProviderSelect
                    serviceKey={sub.key}
                    currentProvider={currentProvider}
                    availability={availabilityFor(sub.key)}
                    onChange={(p) => handleProviderChange(sub.key, p)}
                    saving={isSaving}
                  />
                  <ServiceModelPicker
                    platformServiceKey={sub.key}
                    provider={currentProvider}
                    widthPx={240}
                    platformConfigEndpoint="/ai-tools/service-models/"
                    buildSaveEndpoint={(k) => `/ai-tools/service-models/${k}`}
                    readOnly={isServicePlatformScoped(sub.key)}
                    readOnlyReason={PLATFORM_SCOPED_READONLY_REASON}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Passo 1 — Embedder. Non scelto: card che apre il popup di scelta
  // (EmbedderChoiceWizard). Scelto: riepilogo definitivo + modello chat
  // coerente usato dalla pipeline RAG.
  function renderEmbedderStep() {
    if (embedderProvider === null) {
      return (
        <div data-service-row="embeddings">
          <EmbedderGateOverlay onChooseEmbedder={openEmbedderWizard} />
        </div>
      );
    }
    const vectorSize = embedderProvider === 'gemini' ? 3072 : 1536;
    const lockedDate = embedderLockedAt ? new Date(embedderLockedAt).toLocaleString('it-IT') : '—';
    const providerLabel = embedderProvider === 'gemini' ? 'Google Gemini' : 'OpenAI';
    const isGemini = embedderProvider === 'gemini';
    const chatValue = isGemini ? geminiChatModel : openaiChatModel;
    const chatSaving = isGemini ? savingGeminiChat : savingOpenaiChat;
    const onChatChange = isGemini ? handleSaveGeminiChatModel : handleSaveOpenaiChatModel;
    return (
      <div
        data-service-row="embeddings"
        data-embedder-locked="true"
        data-testid="embedder-locked-info-card"
        className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-950/10 p-4"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg shrink-0">
            <Lock className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{EMBEDDINGS_ITEM.label}</p>
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800 text-[10px] px-1.5 py-0">
                Scelta definitiva
              </Badge>
              {embedderDegraded && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] px-1.5 py-0" data-testid="embedder-degraded-badge">
                  API key mancante
                </Badge>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              La base della ricerca nella Knowledge Base. Per cambiarlo serve re-indicizzare tutta la KB.
            </p>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[11px]">
              <div><span className="text-gray-500">Provider:</span> <span className="font-mono text-gray-800 dark:text-gray-200">{providerLabel}</span></div>
              <div><span className="text-gray-500">Modello:</span> <span className="font-mono text-gray-800 dark:text-gray-200">{embedderModel ?? '—'}</span></div>
              <div><span className="text-gray-500">Dimensione vettori:</span> <span className="font-mono text-gray-800 dark:text-gray-200">{vectorSize} dim</span></div>
              <div><span className="text-gray-500">Scelto il:</span> <span className="text-gray-800 dark:text-gray-200">{lockedDate}</span></div>
            </div>
            <div
              className={`mt-3 p-2 rounded border space-y-1 ${isGemini
                ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900'
                : 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900'}`}
            >
              <div className="flex items-center gap-2">
                <Globe className={`h-3 w-3 ${isGemini ? 'text-blue-600' : 'text-emerald-600'}`} />
                <span className={`font-semibold text-[11px] ${isGemini ? 'text-blue-800 dark:text-blue-300' : 'text-emerald-800 dark:text-emerald-300'}`}>
                  Modello chat {isGemini ? 'Gemini' : 'OpenAI'} della pipeline RAG
                </span>
              </div>
              <p className={`text-[10px] ${isGemini ? 'text-blue-700 dark:text-blue-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                Usato sempre da sintesi, scomposizione e ricerca avanzata (RAG Plus); dal rerank quando il suo provider coincide con l'embedder.
              </p>
              <EmbedderChatModelSelect
                provider={embedderProvider}
                value={chatValue}
                saving={chatSaving}
                onChange={(m) => { void onChatChange(m); }}
              />
              <p
                className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-1"
                data-testid={`${embedderProvider}-chat-model-wired-badge`}
              >
                ✓ Attivo per tutto il tenant (aggiornato su tutti i server).
              </p>
              {isGemini && (
                <p className="text-[10px] text-blue-700 dark:text-blue-400 mt-0.5">
                  Limiti file Gemini: immagini ≤ 20 MB · audio ≤ 180 s · video ≤ 81 s · PDF ≤ 50 MB. Quota giornaliera in Costi & Limiti.
                </p>
              )}
            </div>
            <FunctionHelp testId="service-details-embeddings" content={EMBEDDINGS_ITEM.longDescription} />
          </div>
        </div>
      </div>
    );
  }

  function renderServiceRow(item: ServiceItem) {
    const Icon = item.icon;
    const currentProvider = currentProviderOf(item.key);
    const meta = PROVIDER_META[currentProvider] ?? PROVIDER_META.none!;
    const isFixed = item.fixedProvider !== undefined;
    const isSaving = saving === item.key;
    const embedderChatModel = embedderChatModelOf(embedderProvider, geminiChatModel, openaiChatModel);
    const ragPlatformKey = RAG_SERVICE_KEYS[item.key] ?? item.key;
    const isPipelineFollower = RAG_CHAT_FOLLOWER_KEYS.has(item.key) && embedderChatModel !== null;
    const hasExplicitChoice = isPipelineFollower && explicitServiceKeys.has(ragPlatformKey);
    const followsEmbedderChat = isPipelineFollower && !hasExplicitChoice && !customizingKeys.has(item.key);

    // Gating embedder per `rag_synthesis_multimodal`: row attiva
    // SOLO se l'embedder del tenant è Gemini (coerenza spazio semantico
    // multimodal 3072d). Su embedder diverso il select resta disabilitato +
    // tooltip esplicito; backend rifiuta a 400 il save (assertEmbedderCoherence).
    // I chunk immagine esistono solo con embedder Gemini: con un altro embedder
    // l'attivazione non avrebbe effetto ed è bloccata (spegnere resta possibile).
    const isMultimodalRow = item.key === 'rag_synthesis_multimodal';
    const isEmbedderIncompatible = isMultimodalRow && embedderProvider !== 'gemini';
    const multimodalActivationBlocked = isEmbedderIncompatible && currentProvider === 'none';
    const incompatibleTooltip = isEmbedderIncompatible
      ? `Richiede l'embedder Gemini: con ${embedderProvider === 'openai' ? 'OpenAI' : 'questo embedder'} la Knowledge Base ` +
        'non contiene chunk immagine e la sintesi testuale (3.) copre tutte le risposte.'
      : null;

    // Recommendation dinamica per i 3 service items RAG
    const dynRec = dynamicRecs?.[item.key];
    const liveRecText = dynRec
      ? [
          dynRec.preferred ? `★ Preferito: ${dynRec.preferred.provider}/${dynRec.preferred.model} — ${dynRec.preferred.reason}` : null,
          ...dynRec.alternatives.map(a => `• ${a.provider}/${a.model}: ${a.reason}`),
          dynRec.note ? `→ ${dynRec.note}` : null,
        ].filter(Boolean).join('\n')
      : null;
    const effectiveRecommendation = liveRecText || item.recommendation;

    // Fallback dichiarativo tenant-scoped per la riga MM.
    const fallbackProvider: string = isMultimodalRow
      ? (data?.services?.rag_synthesis_multimodal_fallback || 'none')
      : 'none';
    const isSavingFallback = saving === 'rag_synthesis_multimodal_fallback';
    // Il reranker accetta solo i provider del motore di rerank.
    const rowAvailabilityRaw = availabilityFor(item.key);
    const rowAvailability: ProviderAvailability = item.key === 'rag_reranker' && rowAvailabilityRaw.status === 'ready'
      ? { status: 'ready', providers: rowAvailabilityRaw.providers.filter((p) => RERANK_SELECTABLE.has(p.id)) }
      : rowAvailabilityRaw;

    return (
      <Fragment key={item.key}>
      <div
        data-service-row={item.key}
        data-embedder-incompatible={isEmbedderIncompatible ? 'true' : undefined}
        title={incompatibleTooltip ?? undefined}
        className={`flex flex-wrap items-center justify-between gap-3 p-4 transition-shadow rounded ${isFixed ? 'opacity-70' : ''} ${multimodalActivationBlocked ? 'opacity-60' : ''}`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-[16rem]">
          <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg shrink-0">
            <Icon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.label}</p>
              {/* Badge modalità reranker (multimodale nativo vs text-only ibrido) */}
              {item.key === 'rag_reranker' && currentProvider !== 'none' && (
                <Badge
                  variant="outline"
                  className={`text-[9px] px-1 py-0 ${
                    currentProvider === 'google'
                      ? 'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-800'
                      : 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-800'
                  }`}
                  title={
                    currentProvider === 'google'
                      ? 'Gemini riceve i binari (immagini/audio/video) via inline_data: scoring nativo multimodale.'
                      : 'Provider text-only: i chunk multimodali bypassano il rerank LLM e mantengono lo score retriever (boost rerank_multimodal_boost).'
                  }
                >
                  {currentProvider === 'google' ? 'Multimodale nativo' : 'Text-only ibrido'}
                </Badge>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">{item.desc}</p>
            <FunctionHelp
              testId={`service-details-${item.key}`}
              content={{ ...item.longDescription, advice: effectiveRecommendation }}
            />
            {item.key === 'ocr' && currentProvider === 'google' && (
              <div className="flex items-center gap-1 mt-1">
                <Lock className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                <p className="text-[10px] text-blue-600 dark:text-blue-400">Bypass multimodale Gemini attivo: il worker OCR è disabilitato per i nuovi documenti scansionati.</p>
              </div>
            )}
            {item.key === 'ocr' && ocrCoherence && !ocrCoherence.coherent && (
              <div
                className={
                  ocrCoherence.severity === 'warn'
                    ? 'mt-2 p-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800'
                    : 'mt-2 p-2 rounded border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800'
                }
                data-testid="ocr-coherence-warning"
              >
                <div className="min-w-0">
                  <p className={
                    ocrCoherence.severity === 'warn'
                      ? 'text-[11px] text-amber-700 dark:text-amber-300 font-medium'
                      : 'text-[11px] text-red-700 dark:text-red-300 font-medium'
                  }>
                    {ocrCoherence.severity === 'warn' ? 'Coerenza OCR consigliata' : 'Configurazione OCR incoerente'}
                  </p>
                  <p className={
                    ocrCoherence.severity === 'warn'
                      ? 'text-[10px] text-amber-600 dark:text-amber-400 mt-0.5'
                      : 'text-[10px] text-red-600 dark:text-red-400 mt-0.5'
                  }>
                    {ocrCoherence.reason}
                  </p>
                </div>
              </div>
            )}
            {/* Synthesis MM auto-suggest: quando embedder=Gemini,
                provider attuale=none e ci sono image chunks nel tenant, mostra
                banner che propone l'attivazione del provider Gemini. */}
            {isMultimodalRow && embedderProvider === 'gemini' && currentProvider === 'none' && multimodalCounts && multimodalCounts.image_chunks > 0 && (
              <div
                className="mt-2 p-2 rounded border border-sky-300 bg-sky-50 dark:bg-sky-950/30 dark:border-sky-800 flex items-start justify-between gap-2"
                data-testid="synthesis-mm-auto-suggest"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] text-sky-800 dark:text-sky-200 font-medium">
                    Auto-attiva consigliato: {multimodalCounts.image_chunks} chunk immagine indicizzati
                  </p>
                  <p className="text-[10px] text-sky-700 dark:text-sky-300 mt-0.5">
                    Embedder Gemini + Synthesis MM su <strong>none</strong>: i chunk visuali non vengono sintetizzati. Attiva il provider <strong>Gemini</strong> per la sintesi multimodale nativa (3072d).
                  </p>
                </div>
                <button
                  type="button"
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-sky-400 text-sky-800 dark:text-sky-200 bg-sky-100/40 dark:bg-sky-900/30 hover:bg-sky-100 dark:hover:bg-sky-900/50 disabled:opacity-50 text-[10px] font-medium"
                  onClick={() => { void handleProviderChange(item.key, 'google'); }}
                  disabled={isSaving}
                  data-testid="synthesis-mm-activate-cta"
                  title="Imposta rag_synthesis_multimodal → google (Gemini)."
                >
                  {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  Attiva Gemini
                </button>
              </div>
            )}
            {item.key === 'ocr' && currentProvider === 'google' && ocrCoherence && ocrCoherence.coherent && (
              <Badge
                className="mt-2 bg-green-100 text-green-700 border-green-200 text-[10px]"
                data-testid="ocr-coherence-ok"
              >
                Coerente con chat model {ocrCoherence.chat_model || 'Gemini'}
              </Badge>
            )}
            {item.key === 'ocr' && currentProvider === 'none' && (
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">OCR disattivato: i PDF scansionati verranno marcati come <code>no_provider</code> e non verranno indicizzati.</p>
            )}
            {item.key === 'ocr' && currentProvider !== 'none' && currentProvider !== 'google' && ocrStatus && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {!ocrStatus.api_key_present && (
                  <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]" data-testid="ocr-key-missing">
                    API key mancante
                  </Badge>
                )}
                {ocrStatus.api_key_present && !ocrStatus.api_key_valid_format && (
                  <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 text-[10px]" data-testid="ocr-key-invalid">
                    Formato API key sospetto
                  </Badge>
                )}
              </div>
            )}
            {item.key === 'ocr' && currentProvider !== 'none' && (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="ocr-timeout-row">
                <label className="text-[10px] text-gray-600 dark:text-gray-400">Timeout max OCR (min):</label>
                <Input
                  type="number"
                  min={5}
                  max={60}
                  value={ocrTimeoutDraft}
                  onChange={(e) => setOcrTimeoutDraft(e.target.value)}
                  className="h-7 w-20 text-xs"
                  data-testid="ocr-timeout-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  disabled={saving === 'ocr_timeout' || Number(ocrTimeoutDraft) === ocrTimeoutMin}
                  onClick={handleSaveOcrTimeout}
                  data-testid="ocr-timeout-save"
                >
                  {saving === 'ocr_timeout' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Salva'}
                </Button>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                  Default adattivo: 60s + 15s/pagina (cap configurabile 5–60 min).
                </span>
              </div>
            )}
            {item.key === 'ocr' && ocrFailures.length > 0 && (
              <div className="mt-3" data-testid="ocr-failures-list">
                <div className="flex items-center gap-2">
                  <p className="text-[11px] font-medium text-gray-700 dark:text-gray-300">
                    Ultimi errori OCR
                  </p>
                  <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">
                    {ocrFailuresTotal} ultime 24h
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-[10px] px-2"
                    onClick={() => fetchOcrFailures()}
                    data-testid="ocr-failures-refresh"
                  >
                    Aggiorna
                  </Button>
                  <span className="text-[10px] text-gray-400">aggiornamento auto ogni 30s</span>
                </div>
                <ul className="mt-1 space-y-1">
                  {ocrFailures.map(f => {
                    const ageMs = Date.now() - new Date(f.created_at).getTime();
                    const mins = Math.max(0, Math.round(ageMs / 60000));
                    const relative = mins < 1
                      ? 'adesso'
                      : mins < 60
                        ? `${mins} min fa`
                        : mins < 60 * 24
                          ? `${Math.round(mins / 60)} h fa`
                          : `${Math.round(mins / 60 / 24)} g fa`;
                    return (
                      <li
                        key={f.id}
                        className="text-[10px] text-gray-600 dark:text-gray-400 flex items-start gap-2"
                      >
                        <span
                          className={
                            f.severity === 'high'
                              ? 'text-red-600 dark:text-red-400 font-semibold'
                              : f.severity === 'medium'
                                ? 'text-orange-600 dark:text-orange-400'
                                : 'text-yellow-600 dark:text-yellow-400'
                          }
                        >
                          [{f.severity}]
                        </span>
                        <TooltipProvider>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate flex-1 cursor-help">{f.message}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-md">
                              <p className="text-xs whitespace-pre-wrap">{f.message}</p>
                              {f.metric_value != null && (
                                <p className="text-[10px] text-text-muted mt-1">Pagine coinvolte: {f.metric_value}</p>
                              )}
                              <p className="text-[10px] text-text-muted mt-1">{new Date(f.created_at).toLocaleString()}</p>
                            </TooltipContent>
                          </UITooltip>
                        </TooltipProvider>
                        <span className="shrink-0 text-gray-400" title={new Date(f.created_at).toLocaleString()}>
                          {relative}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {followsEmbedderChat ? (
            <div className="text-right" data-testid={`embedder-chat-follower-${item.key}`}>
              <Badge className={`${meta.color} text-xs`}>
                {meta.label} · {embedderChatModel}
              </Badge>
              <p className="text-[10px] text-muted-foreground mt-1">Segue il modello chat della pipeline RAG</p>
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-[11px]"
                onClick={() => setCustomizingKeys((prev) => new Set(prev).add(item.key))}
                data-testid={`customize-${item.key}`}
              >
                Scegli un altro modello
              </Button>
            </div>
          ) : isFixed ? (
            <Badge className={`${meta.color} text-xs`}>
              {meta.label} (fisso)
            </Badge>
          ) : (
            <ServiceProviderSelect
              serviceKey={item.key}
              currentProvider={currentProvider}
              availability={rowAvailability}
              allowNone={item.canDisable === true}
              onChange={(p) => handleProviderChange(item.key, p)}
              saving={isSaving || (item.key === 'rag_reranker' && saving === 'rerank_mode')}
              disabled={multimodalActivationBlocked}
              withToggle={isMultimodalRow}
              disabledReasons={(() => {
                // Motivi di blocco:
                //   - Google non configurato (Gemini connection invalid)
                const reasons: Record<string, string> = {};
                if (data?.google?.connection_valid !== true) {
                  reasons.google = 'Configura Google AI in Provider AI';
                }
                return Object.keys(reasons).length > 0 ? reasons : undefined;
              })()}
            />
          )}
          {isPipelineFollower && !followsEmbedderChat && (
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-[11px]"
              disabled={isSaving}
              onClick={() => {
                if (hasExplicitChoice) void handleFollowPipeline(item.key);
                else setCustomizingKeys((prev) => { const n = new Set(prev); n.delete(item.key); return n; });
              }}
              data-testid={`follow-pipeline-${item.key}`}
            >
              {hasExplicitChoice ? 'Usa il modello della pipeline' : 'Annulla'}
            </Button>
          )}
        </div>
        {/* Cascade Provider→Model: il picker filtra il recommender
            sul provider corrente del servizio e salva su platform_model_config. */}
        {(() => {
          const platformKey = PLATFORM_SERVICE_KEY_MAP[item.key];
          if (!platformKey) return null;
          if (currentProvider === 'none' || followsEmbedderChat) return null;
          return (
            <div className="shrink-0 max-w-full overflow-x-auto" data-service-model-picker={item.key}>
              <ServiceModelPicker
                platformServiceKey={platformKey}
                provider={currentProvider}
                widthPx={260}
                platformConfigEndpoint="/ai-tools/service-models/"
                buildSaveEndpoint={(k) => `/ai-tools/service-models/${k}`}
                readOnly={isServicePlatformScoped(platformKey)}
                readOnlyReason={PLATFORM_SCOPED_READONLY_REASON}
              />
            </div>
          );
        })()}
      </div>
      {isMultimodalRow && currentProvider !== 'none' && (
        <div
          data-service-row="rag_synthesis_multimodal_fallback"
          className="ml-12 mb-2 flex items-center gap-3 px-4 py-2.5 rounded border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20"
        >
          <Badge className="bg-blue-500 text-white text-[10px] shrink-0">Fallback (opzionale)</Badge>
          <span className="text-xs text-slate-600 dark:text-slate-300 flex-1 min-w-0">
            Se il primario fallisce, prova questo provider+modello (vision-capable, diverso dal primario).
            Lascia OFF per la chain auto cross-provider.
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <ServiceProviderSelect
              serviceKey="rag_synthesis_multimodal_fallback"
              currentProvider={fallbackProvider}
              availability={availabilityFor('rag_synthesis_multimodal_fallback')}
              onChange={(p) => handleProviderChange('rag_synthesis_multimodal_fallback', p)}
              saving={isSavingFallback}
              withToggle
            />
            {fallbackProvider !== 'none' && (
              <div data-service-model-picker="rag_synthesis_multimodal_fallback">
                <ServiceModelPicker
                  platformServiceKey="rag_synthesis_multimodal_fallback"
                  provider={fallbackProvider}
                  widthPx={240}
                  platformConfigEndpoint="/ai-tools/service-models/"
                  buildSaveEndpoint={(k) => `/ai-tools/service-models/${k}`}
                />
              </div>
            )}
          </div>
        </div>
      )}
      </Fragment>
    );
  };

  function renderRerankModeRow() {
    const isSaving = saving === 'rerank_mode';
    const requested = rerankMode?.requested ?? 'disabled';
    const parsed = parseRerankRequestedMode(requested);
    const enabled = parsed.kind !== 'disabled';
    const crossCheck = parsed.kind === 'hybrid';
    const fromService = data?.services?.rag_reranker;
    const primaryProvider: RerankProviderId =
      parsed.primary ?? (isRerankProviderId(fromService) ? fromService : 'openai');
    const secondaryProvider: RerankProviderId | null = parsed.secondary;
    const totalMm = multimodalCounts?.total_multimodal ?? 0;
    const hasMmChunks = totalMm > 0;
    const showMultimodalSection = enabled && embedderProvider === 'gemini' && hasMmChunks;
    const openaiMissingHybrid =
      !!rerankMode && crossCheck && !rerankMode.openai_available
      && (primaryProvider === 'openai' || secondaryProvider === 'openai');
    const hybridSurvivor: RerankProviderId | null =
      primaryProvider === 'openai' ? secondaryProvider : primaryProvider;
    const singlePrimaryDown =
      !!rerankMode && parsed.kind === 'single'
      && primaryProvider === 'openai' && !rerankMode.openai_available;

    const isTextOnlyPrimary = primaryProvider === 'openai' || primaryProvider === 'claude';

    // Guardie cross-check.
    //   (1) Rerank attivo: il cross-check ha senso solo se la modalità rerank
    //       è ≠ disabled (almeno un primario sta lavorando).
    //   (2) ≥2 provider AI canonici attivi tra openai/claude/google:
    //       servono almeno due ranker distinti per RRF (vincolo a≠b nel parser
    //       backend). Intersezione stretta `RERANK_PROVIDER_IDS ∩ activeProviders`:
    //       conta solo provider con api key configurata sul tenant; nessuna
    //       eccezione hardcoded (google incluso solo se realmente attivo).
    const activeRerankProviders = RERANK_PROVIDER_IDS.filter((p) => {
      if (p === 'openai') return !!data?.openai?.active;
      return activeProviders.includes(p);
    });
    const crossCheckDisabledReason: string | null =
      !enabled
        ? 'Attiva prima il reranker (seleziona un provider primario).'
        : activeRerankProviders.length < 2
          ? 'Servono almeno 2 provider AI attivi per il cross-check.'
          : null;
    const crossCheckBlocked = crossCheckDisabledReason !== null;

    return (
      <div data-service-row="rerank_mode" className="flex flex-wrap items-start justify-between gap-3 p-4 transition-shadow rounded bg-gray-50/50 dark:bg-gray-900/30">
        <div className="flex items-start gap-3 flex-1 min-w-[16rem]">
          <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg shrink-0">
            <Layers className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Modalità Reranking</p>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{describeRerankMode(parsed)}</p>

            <FunctionHelp testId="service-details-rerank_mode" content={RERANK_MODE_HELP} />


            {/* Warning fallback (cross-check con provider giù) */}
            {openaiMissingHybrid && hybridSurvivor && (
              <div className="mt-1 flex items-start gap-1 text-[11px] text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                <span>OpenAI non disponibile per questo tenant — il cross-check degrada al solo {RERANK_PROVIDER_LABEL[hybridSurvivor]}.</span>
              </div>
            )}
            {singlePrimaryDown && (
              <div className="mt-1 flex items-start gap-1 text-[11px] text-red-700 dark:text-red-400">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                <span>
                  {RERANK_PROVIDER_LABEL[primaryProvider]} non disponibile per questo tenant — il reranking verrà
                  <strong> disattivato</strong>. Configura il provider o attiva il cross-check con un altro modello.
                </span>
              </div>
            )}

            {/* Sotto-sezione Compatibilità multimodale (gemini + chunk MM > 0) */}
            {showMultimodalSection && (
              <details className="mt-2 group" data-testid="rerank-multimodal-section">
                <summary className="text-[11px] text-violet-700 dark:text-violet-300 cursor-pointer hover:underline list-none flex items-center gap-1 select-none">
                  <span className="inline-block transition-transform group-open:rotate-90">▸</span>
                  <span>Compatibilità multimodale ({totalMm} chunk media nel knowledge base)</span>
                </summary>
                <div className="mt-2 ml-3 pl-3 border-l-2 border-violet-200 dark:border-violet-800 space-y-3 text-[11px]">
                  {isTextOnlyPrimary && (
                    <div className="text-gray-700 dark:text-gray-300">
                      Embedder Gemini + reranker text-only (<strong>{RERANK_PROVIDER_LABEL[primaryProvider]}</strong>):
                      i chunk immagine/audio/video bypassano il rerank LLM e mantengono lo score del retriever × Boost MM.
                      Per scoring nativo su media, passa il reranker a <strong>Gemini</strong>.
                    </div>
                  )}
                  <div className="space-y-1">
                    <label className="block text-[11px] font-medium text-gray-900 dark:text-gray-100">Boost multimodale</label>
                    <p className="text-[10px] text-gray-600 dark:text-gray-400">
                      Peso relativo dei chunk media rispetto a quelli testuali (1.0 = neutro). Effetto solo con reranker text-only.
                    </p>
                    <div className="flex items-center gap-2" data-testid="rerank-multimodal-boost-control">
                      <Input
                        type="number"
                        step={0.05}
                        min={0.5}
                        max={2.0}
                        value={rerankBoostDraft}
                        onChange={(e) => setRerankBoostDraft(e.target.value)}
                        onBlur={() => {
                          const v = Number(rerankBoostDraft);
                          if (Number.isFinite(v) && Math.abs(v - rerankBoost) > 1e-6) {
                            void saveRerankBoost(Math.round(v * 100) / 100);
                          } else {
                            setRerankBoostDraft(rerankBoost.toFixed(2));
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
                        disabled={savingBoost}
                        className="w-[78px] h-7 text-xs text-right"
                        data-testid="rerank-multimodal-boost-input"
                      />
                      {savingBoost && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                      <span className="text-[10px] text-gray-500 dark:text-gray-400">range 0.5 – 2.0</span>
                    </div>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0 min-w-[200px]">
          {/* Toggle Cross-check unico. ON/OFF rispetto al modello scelto nella
              riga "Reranker" qui sopra. */}
          <div className="flex items-center gap-2">
            <label
              htmlFor="rerank-toggle-crosscheck"
              className="text-[11px] select-none text-gray-700 dark:text-gray-300"
            >
              Cross-check
            </label>
            <TooltipProvider>
              <UITooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Switch
                      id="rerank-toggle-crosscheck"
                      checked={crossCheck}
                      disabled={isSaving || !rerankMode || (crossCheckBlocked && !crossCheck)}
                      onCheckedChange={(v) => { void handleRerankCrossCheckToggle(v); }}
                      data-testid="rerank-toggle-crosscheck"
                    />
                  </span>
                </TooltipTrigger>
                {crossCheckDisabledReason && !crossCheck && (
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-xs">{crossCheckDisabledReason}</p>
                  </TooltipContent>
                )}
              </UITooltip>
            </TooltipProvider>
            {isSaving && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
          </div>

          {/* Picker secondario (provider + modello, visibili solo se cross-check ON).
              Vincolo a≠b: il provider primario è filtrato dalla lista. Provider
              e modello scrivono entrambi sulla riga
              `tenant_service_routing(rag_rerank_secondary)`. */}
          {crossCheck && (
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-gray-700 dark:text-gray-300 whitespace-nowrap select-none">2° modello</label>
              <Select
                value={secondaryProvider ?? ''}
                onValueChange={(v) => {
                  if (isRerankProviderId(v)) void handleRerankSecondaryProviderChange(v);
                }}
                disabled={isSaving}
              >
                <SelectTrigger
                  className="w-[150px] h-8 text-xs"
                  data-testid="rerank-secondary-provider-select"
                >
                  <SelectValue placeholder="Scegli…" />
                </SelectTrigger>
                <SelectContent>
                  {RERANK_PROVIDER_IDS.filter((p) => p !== primaryProvider).map((p) => (
                    <SelectItem key={p} value={p} className="text-xs">{RERANK_PROVIDER_LABEL[p]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {secondaryProvider && (
                <div data-service-model-picker="rag_rerank_secondary">
                  <ServiceModelPicker
                    platformServiceKey="rag_rerank_secondary"
                    provider={secondaryProvider}
                    widthPx={220}
                    platformConfigEndpoint="/ai-tools/service-models/"
                    buildSaveEndpoint={(k) => `/ai-tools/service-models/${k}`}
                  />
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <AiGovernanceHistory />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Routing Servizi AI</h2>
          <p className="text-sm text-muted-foreground">
            Scegli quale provider e modello AI esegue ogni funzione della piattaforma: parti
            dall'embedder, poi configura le funzioni fase per fase.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <RoutingGuideDialog />
          <Button
            variant="outline"
            size="sm"
            onClick={() => { void fetchStatus(); }}
            disabled={refreshing}
            data-testid="service-routing-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Provider: sola lettura, sempre visibile in cima. */}
      <section className="space-y-3" data-testid="routing-providers-status">
        <StepHeading
          title="Provider"
          subtitle="Chi può eseguire le funzioni AI di questa pagina e quante funzioni usa ciascuno. Chiavi ed endpoint si gestiscono nella scheda Provider AI."
        />
        <ProviderOverview entries={providerEntries} />
      </section>

      {/* Passo 1 — Embedder: titolo fuori, riga richiudibile (chiusa all'apertura)
          come le funzioni del passo 2; la scelta si fa nel popup (EmbedderChoiceWizard). */}
      <section className="space-y-3" data-testid="routing-step-embedder">
        <StepHeading
          step={1}
          title="Embedder della Knowledge Base"
          subtitle="La base di tutta la pipeline RAG. Si sceglie una volta sola: tutte le altre funzioni dipendono da questa scelta."
        />
        <RoutingAccordion
          icon={Database}
          title="Embedder e modello chat della pipeline RAG"
          subtitle="Provider e modello dei vettori della Knowledge Base, modello chat usato da sintesi, scomposizione e ricerca avanzata."
          accent="emerald"
          testId="routing-group-embedder"
        >
          <div className="space-y-3 p-4">
        {renderEmbedderStep()}
        {embedderProvider !== null && embedderDegraded && (
          <div
            data-testid="embedder-degraded-warning"
            className="flex items-start gap-2 p-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="text-sm leading-snug">
              <strong>Embedder degradato</strong>: la pipeline RAG è configurata
              su <strong>{embedderProvider === 'openai' ? 'OpenAI' : 'Gemini'}</strong>{' '}
              ma le credenziali del provider non sono attive. L'ingest e il
              retrieval non funzioneranno finché non viene caricata una API key
              valida nelle Impostazioni Provider.
            </div>
          </div>
        )}
          </div>
        </RoutingAccordion>
      </section>

      {/* Avviso fuori dal blocco disattivato, così resta leggibile (niente
          trasparenza/scala di grigi ereditata). */}
      {embedderProvider === null && (
        <div
          data-testid="service-routing-gated-overlay-message"
          className="sticky top-2 z-20 mx-auto max-w-md rounded-lg border border-amber-300 dark:border-amber-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur shadow-lg px-4 py-3 flex items-start gap-2"
        >
          <Lock className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-sm text-amber-900 dark:text-amber-100 leading-snug">
            <strong>Disponibile dopo aver scelto l'embedder</strong> nel passo 1.
          </p>
        </div>
      )}

      {/* Passo 2 e strumenti avanzati: disponibili dopo la scelta dell'embedder. */}
      <div
        aria-disabled={embedderProvider === null}
        data-testid="service-routing-gated-content"
        className={`relative space-y-6 ${
          embedderProvider === null ? 'pointer-events-none opacity-50 grayscale select-none' : ''
        }`}
      >
        <section className="space-y-3" data-testid="routing-step-services">
          <StepHeading
            step={2}
            title="Funzioni AI, fase per fase"
            subtitle="Per ogni funzione scegli provider e modello. L'ordine segue il percorso dei dati: caricamento della Knowledge Base, risposta alle domande, voce, analisi."
          />
          {renderGroupSection('ingestion')}
          {renderGroupSection('retrieval')}
          <RoutingAccordion
            icon={Sparkles}
            title="Ricerca avanzata e memoria delle risposte"
            subtitle="RAG Plus (riformulazione, risposta ipotetica, paragrafo intero, quantità adattiva) e riuso delle risposte già date."
            accent="indigo"
            testId="routing-group-rag-plus"
          >
            <div className="p-4">
              <RAGPlusSection />
            </div>
          </RoutingAccordion>
          {renderGroupSection('voice')}
          {renderGroupSection('analytics')}
          <RoutingAccordion
            icon={Layers}
            title="Altre funzioni AI"
            subtitle="Modello predefinito dei nuovi agenti, analisi e riassunto file, Landing Builder, valutazione delle risposte, trascrizione del workspace."
            accent="slate"
            testId="routing-group-other"
          >
            <div className="p-3">
              <OtherPlatformServicesSection
                excludeServiceKeys={[
                  ...Object.values(PLATFORM_SERVICE_KEY_MAP),
                  ...EXTRA_EXCLUDED_PLATFORM_KEYS,
                ]}
              />
            </div>
          </RoutingAccordion>
        </section>

        <section className="space-y-3" data-testid="routing-step-advanced">
          <StepHeading
            title="Strumenti avanzati"
            subtitle="Catalogo dei modelli e registro dei consigli automatici. Non servono per configurare le funzioni."
          />
          <RoutingAccordion
            icon={Brain}
            title="Catalogo dei modelli"
            subtitle="Tutti i modelli disponibili per provider, con le loro capacità, e i modelli consigliati per ogni funzione."
            accent="violet"
            testId="routing-group-catalog"
          >
            <div className="p-3">
              <AiModelsCatalogSection />
            </div>
          </RoutingAccordion>
        </section>
      </div>
    </div>
  );
}
